import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SiteNav from '../components/site/SiteNav';
import SiteFooter from '../components/site/SiteFooter';
import Sources from '../components/site/Sources';
import { INDUSTRIES, SERVICES, CONTACT } from '../site/nav';
import { headFor } from '../site/heads';
import { FURNITURE_FAQS } from '../site/faqs';
import { 
  MessageSquare, 
  Truck, 
  Receipt, 
  Star, 
  Globe, 
  CheckCircle,
  ArrowRight,
  ChevronDown,
  Menu,
  X,
  Calendar,
  Mic,
  Phone,
  PhoneOff,
  MapPin,
  Sparkles,
  Send,
  Check,
  Clock,
  User,
  Bot,
  Box,
  Camera,
  Video,
  TrendingUp,
  FileText,
  Heart,
  Volume2,
  VolumeX,
  ShoppingCart,
  Monitor,
} from 'lucide-react';

// ============================================
// SCROLL PROGRESS HOOK
// ============================================
// ============================================
// IN-VIEW HOOK - lights a section up the moment
// it enters the viewport, regardless of page length
// ============================================
function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    // Below lg a story section is taller than the screen, so 20% of it is a
    // whole headline's worth of scrolling before anything shows. Use a sliver.
    const t = window.innerWidth < 1024 ? Math.min(threshold, 0.06) : threshold;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: t }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, inView];
}

// Counts a number up from 0 once `active` flips true (mirrors the booth
// loop's climbing counters). Respects prefers-reduced-motion by jumping
// straight to the target.
function useCountUp(target, active, { duration = 1300, delay = 0 } = {}) {
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!active || started.current) return undefined;
    started.current = true;

    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setValue(target);
      return undefined;
    }

    let raf;
    const t0 = performance.now() + delay;
    const tick = (now) => {
      const p = Math.min(1, Math.max(0, (now - t0) / duration));
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic, same feel as the loop
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration, delay]);

  return value;
}

// ============================================
// HERO PHONE CONTENT - CINEMATIC THREE-CHAPTER DEMO
// Act 1 · Sales call: a new shopper calls - the AI quotes the deal,
//         texts the picture, pre-screens, notes the floor team.
// Act 2 · Customer care: a known customer calls - the AI reads her
//         itemized receipt and answers at the item level.
// Act 3 · Review autopilot: survey texts out, 5 stars land on Google.
// ============================================

// --- Easy-to-edit assets & copy -------------------------------
const HERO_LOCATION_IMG_ID = '1wdHdpPUPiIKtC9yCW3VrdEln-IIPxvT6'; // store/location photo (Google Drive file id)

// Google Drive image with graceful fallback chain
function HeroDriveImg({ id, alt, className }) {
  const [srcIdx, setSrcIdx] = useState(0);
  const sources = [
    `https://drive.google.com/thumbnail?id=${id}&sz=w800`,
    `https://lh3.googleusercontent.com/d/${id}`,
  ];
  if (srcIdx >= sources.length) {
    return (
      <div className={`${className} bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center`}>
        <Camera className="w-5 h-5 text-gray-500" />
      </div>
    );
  }
  return (
    <img
      src={sources[srcIdx]}
      alt={alt}
      loading="lazy"
      referrerPolicy="no-referrer"
      className={className}
      onError={() => setSrcIdx((i) => i + 1)}
    />
  );
}

// ============================================
function HeroPhoneContent() {
  // scene: 'boot' | 'agent' | 'review' - beat: step inside the scene
  // Story order: the phone rings → AI closes the new shopper →
  // AI cares for the existing customer → the 5-star review lands on autopilot.
  const [scene, setScene] = useState('boot');
  const [beat, setBeat] = useState(0);
  const [rail, setRail] = useState({ calla: 0, callb: 0, after: 0 });
  const [callSec, setCallSec] = useState(0);

  const timersRef = useRef([]);
  const loopStartRef = useRef(Date.now());

  // --- Master timeline (one full loop ≈ 31.0s) ---
  const CALLA_T0 = 700;
  const CALLB_T0 = 10800;
  const AFTER_T0 = 22000;
  const LOOP_MS = 31000;

  useEffect(() => {
    const TIMELINE = [
      { t: 0, scene: 'boot', beat: 0 },

      // ---- ACT 1 · THE SALES CALL ----
      { t: 700, scene: 'agent', beat: 0 },     // incoming call, matching caller ID
      { t: 1900, scene: 'agent', beat: 1 },    // no match - brand-new shopper
      { t: 3100, scene: 'agent', beat: 2 },    // answered → live, AI greeting
      { t: 4300, scene: 'agent', beat: 3 },    // "how much is the gray sectional?"
      { t: 6000, scene: 'agent', beat: 4 },    // AI quotes the current deal, offers a photo
      { t: 7800, scene: 'agent', beat: 5 },    // the photo, texted mid-call
      { t: 9200, scene: 'agent', beat: 6 },    // pre-screened → note left for the floor team

      // ---- ACT 2 · CUSTOMER CARE ----
      { t: 10800, scene: 'agent', beat: 7 },   // incoming call, matching caller ID
      { t: 11700, scene: 'agent', beat: 8 },   // match: known customer + receipt #
      { t: 12900, scene: 'agent', beat: 9 },   // answered, personalized greeting
      { t: 14000, scene: 'agent', beat: 10 },  // caller asks about her delivery
      { t: 15200, scene: 'agent', beat: 11 },  // AI searching receipts…
      { t: 16100, scene: 'agent', beat: 12 },  // receipt card found - item level
      { t: 17500, scene: 'agent', beat: 13 },  // AI answers with the delivery window
      { t: 19200, scene: 'agent', beat: 14 },  // delivery details texted
      { t: 20500, scene: 'agent', beat: 15 },  // wrap-up: zero staff time

      // ---- ACT 3 · REVIEW AUTOPILOT ----
      { t: 22000, scene: 'review', beat: 0 },  // survey texts automatically
      { t: 23400, scene: 'review', beat: 1 },  // customer taps 5 stars
      { t: 25200, scene: 'review', beat: 2 },  // Google review posted
      { t: 27000, scene: 'review', beat: 3 },  // the full loop, on autopilot
    ];

    const clearAll = () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };

    const run = () => {
      clearAll();
      loopStartRef.current = Date.now();
      TIMELINE.forEach(({ t, scene: s, beat: b }) => {
        timersRef.current.push(setTimeout(() => { setScene(s); setBeat(b); }, t));
      });
      timersRef.current.push(setTimeout(run, LOOP_MS));
    };

    run();

    // Chapter rail + live call timer - both driven off the master clock
    const CALLA_ANSWER = 3100;   // beat 2
    const CALLA_END = 10800;     // beat 7
    const CALLB_ANSWER = 12900;  // beat 9
    const CALLB_END = 20500;     // beat 15
    const railTick = setInterval(() => {
      const e = Date.now() - loopStartRef.current;
      const clamp = (v) => Math.max(0, Math.min(1, v));
      setRail({
        calla: clamp((e - CALLA_T0) / (CALLB_T0 - CALLA_T0)),
        callb: clamp((e - CALLB_T0) / (AFTER_T0 - CALLB_T0)),
        after: clamp((e - AFTER_T0) / (LOOP_MS - AFTER_T0)),
      });
      if (e >= CALLB_ANSWER) {
        setCallSec(Math.floor((Math.min(e, CALLB_END) - CALLB_ANSWER) / 1000));
      } else if (e >= CALLA_ANSWER) {
        setCallSec(Math.floor((Math.min(e, CALLA_END) - CALLA_ANSWER) / 1000));
      } else {
        setCallSec(0);
      }
    }, 120);

    return () => { clearAll(); clearInterval(railTick); };
  }, []);

  const fmtSec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // Which call is on screen
  const isCallA = scene === 'agent' && beat <= 6;   // brand-new shopper
  const isIncoming = scene === 'agent' && (beat <= 1 || beat === 7 || beat === 8);
  const callEnded = scene === 'agent' && (beat === 6 || beat === 15);
  const callLive = scene === 'agent' && ((beat >= 2 && beat <= 5) || (beat >= 9 && beat <= 14));

  return (
    <div className="hero-cine h-full flex flex-col bg-gray-950 relative overflow-hidden">
      <style>{`
        @keyframes heroWave { 0% { transform: scaleY(0.35); } 100% { transform: scaleY(1); } }
        @keyframes heroDropIn {
          0% { opacity: 0; transform: translateY(-34px) rotate(-7deg) scale(1.06); }
          70% { opacity: 1; transform: translateY(3px) rotate(1.5deg) scale(0.99); }
          100% { opacity: 1; transform: translateY(0) rotate(0deg) scale(1); }
        }
        @keyframes heroDropInAlt {
          0% { opacity: 0; transform: translateY(-34px) rotate(6deg) scale(1.06); }
          70% { opacity: 1; transform: translateY(3px) rotate(-1.5deg) scale(0.99); }
          100% { opacity: 1; transform: translateY(0) rotate(0deg) scale(1); }
        }
        @keyframes heroShine { 0% { transform: translateX(-130%) skewX(-18deg); } 100% { transform: translateX(260%) skewX(-18deg); } }
        @keyframes heroScanY { 0% { top: 3%; opacity: 0; } 12% { opacity: 1; } 88% { opacity: 1; } 100% { top: 92%; opacity: 0; } }
        @keyframes heroPop { 0% { opacity: 0; transform: scale(0.92) translateY(6px); } 100% { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes heroFill { from { width: 0%; } to { width: 100%; } }
        @keyframes heroGrad { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        @keyframes heroFloatUp {
          0% { opacity: 0; transform: translate(0, 0) scale(0.6); }
          12% { opacity: 1; }
          35% { transform: translate(-7px, -26px) scale(1); }
          65% { opacity: 0.9; transform: translate(5px, -48px) scale(1); }
          100% { opacity: 0; transform: translate(-3px, -74px) scale(0.9); }
        }
        @keyframes heroToast {
          0% { opacity: 0; transform: translateY(6px); }
          8% { opacity: 1; transform: translateY(0); }
          72% { opacity: 1; }
          88%, 100% { opacity: 0; }
        }
        @keyframes heroBannerIn {
          0% { opacity: 0; transform: translateY(-18px) scale(0.97); }
          60% { opacity: 1; transform: translateY(2px) scale(1); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .hero-pop { animation: heroPop 0.4s cubic-bezier(0.2, 0.9, 0.3, 1.2) forwards; }
        @media (prefers-reduced-motion: reduce) {
          .hero-cine *, .hero-cine *::before, .hero-cine *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      {/* Ambient background glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full blur-3xl bg-blue-500/10" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full blur-3xl bg-blue-500/10" />
      </div>

      {/* ============ BOOT ============ */}
      {scene === 'boot' && (
        <div className="h-full flex items-center justify-center animate-fadeIn">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse shadow-2xl shadow-blue-500/40">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <p className="text-white font-bold text-lg">Exitium</p>
            <p className="text-gray-500 text-sm">Watch it work</p>
          </div>
        </div>
      )}

      {/* ============ ACT 2 · INCOMING CALL SCREEN ============ */}
      {isIncoming && (
        <div className="h-full flex flex-col items-center justify-center px-5 pb-10 bg-gradient-to-br from-gray-950 via-blue-950/40 to-gray-950 animate-fadeIn relative">
          <p className="text-blue-400 text-[10px] uppercase tracking-[0.2em] mb-4">Incoming call</p>

          <div className="relative mb-5">
            <span className="absolute inset-0 rounded-full bg-blue-500/25 animate-ping" />
            <span className="absolute -inset-3 rounded-full bg-blue-500/10 animate-pulse" />
            <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-2xl shadow-blue-500/50">
              {!isCallA && beat >= 8 ? (
                <span className="text-white font-bold text-xl">ML</span>
              ) : (
                <Phone className="w-8 h-8 text-white" />
              )}
            </div>
          </div>

          <p className="text-white text-lg font-semibold mb-0.5">
            {!isCallA && beat >= 8 ? 'Maria Lopez' : isCallA ? '+1 (415) 555-0142' : '+1 (209) 555-0184'}
          </p>
          <p className="text-gray-400 text-xs mb-5">
            {!isCallA && beat >= 8 ? '+1 (209) 555-0184' : 'Unknown caller'}
          </p>

          {/* Caller-ID intelligence - recognizes returning customers, flags new shoppers */}
          {(beat === 0 || beat === 7) && (
            <div className="hero-pop flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full">
              <Sparkles className="w-3 h-3 text-blue-300 animate-spin" style={{ animationDuration: '2s' }} />
              <span className="text-blue-200 text-[10px] font-medium">Matching caller ID</span>
              <span className="flex gap-0.5">
                <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" />
                <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
              </span>
            </div>
          )}
          {beat === 1 && (
            <div className="hero-pop flex items-center gap-2 px-3 py-1.5 bg-amber-500/15 border border-amber-500/30 rounded-full shadow-lg shadow-amber-500/20">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              <span className="text-amber-200 text-[10px] font-bold">New shopper · first-time caller</span>
            </div>
          )}
          {beat === 8 && (
            <div className="hero-pop flex items-center gap-2 px-3 py-1.5 bg-green-500/15 border border-green-500/30 rounded-full shadow-lg shadow-green-500/20">
              <Check className="w-3.5 h-3.5 text-green-400" />
              <span className="text-green-300 text-[10px] font-bold">Known customer · Receipt #4127 on file</span>
            </div>
          )}

          <div className="flex items-center gap-12 mt-7">
            <div aria-hidden="true" className="w-12 h-12 rounded-full bg-red-500/90 flex items-center justify-center shadow-lg">
              <PhoneOff className="w-5 h-5 text-white" />
            </div>
            <div aria-hidden="true" className="relative w-12 h-12 rounded-full bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/40">
              <span className="absolute inset-0 rounded-full bg-green-400/50 animate-ping" />
              <Phone className="w-5 h-5 text-white relative" />
            </div>
          </div>

          <p className="text-blue-400/90 text-[10px] mt-5 flex items-center gap-1.5">
            <Bot className="w-3 h-3" />
            Your AI picks up every call, day or night
          </p>
        </div>
      )}

      {/* ============ ACT 2 · LIVE CALL ============ */}
      {scene === 'agent' && !isIncoming && (
        <div className="h-full flex flex-col animate-fadeIn">
          {/* Call header */}
          <div className="px-3.5 py-2.5 border-b border-blue-500/25 bg-gradient-to-r from-blue-500/15 to-blue-600/10 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="relative flex-shrink-0">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                  {isCallA ? <User className="w-4 h-4 text-white" /> : <span className="text-white font-bold text-[11px]">ML</span>}
                </div>
                {callLive && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-gray-950" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-[13px] leading-tight truncate">
                  {isCallA ? 'New Shopper' : 'Maria Lopez'}
                </p>
                <p className="text-blue-300/80 text-[9px] truncate">
                  {isCallA ? 'First-time caller' : 'Customer · Receipt #4127'}
                </p>
              </div>
              {/* The pill IS the waveform: animated bars are the "live" signal,
                  so the 288px row never has to fit a separate soundbar. */}
              <span
                aria-label={callEnded ? 'Call ended' : 'Live call'}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wide flex-shrink-0 tabular-nums ${callEnded ? 'bg-white/10 text-gray-300' : 'bg-green-500/20 text-green-300'}`}
              >
                {!callEnded && (
                  <span className="flex items-end gap-[2px] h-2.5" aria-hidden="true">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <span
                        key={i}
                        className="w-[2px] rounded-full bg-green-300"
                        style={{
                          height: `${34 + Math.abs(Math.sin(i * 1.7)) * 58}%`,
                          animation: `heroWave 0.65s ease-in-out ${i * 0.08}s infinite alternate`,
                        }}
                      />
                    ))}
                  </span>
                )}
                {callEnded ? `Ended ${fmtSec(callSec)}` : fmtSec(callSec)}
              </span>
            </div>
          </div>

          {/* Transcript */}
          <div className="flex-1 px-3 pt-2.5 pb-10 space-y-2 overflow-hidden min-h-0">

            {/* ---- CALL A: new shopper who saw the post ---- */}
            {isCallA && (
              <>
                {beat >= 2 && beat < 5 && (
                  <div className="flex justify-start animate-slideUp">
                    <div className="max-w-[85%] bg-gray-800 rounded-2xl rounded-bl-sm px-3 py-1.5 border border-white/5">
                      <p className="text-white text-[11px] leading-relaxed">Thanks for calling! How can I help you today?</p>
                    </div>
                  </div>
                )}
                {beat >= 3 && (
                  <div className="flex justify-end animate-slideUp">
                    <div className="max-w-[85%] bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl rounded-br-sm px-3 py-1.5 shadow-lg">
                      <p className="text-white text-[11px]">Hi! How much is the gray sectional?</p>
                    </div>
                  </div>
                )}
                {beat >= 4 && (
                  <div className="flex justify-start animate-slideUp">
                    <div className="max-w-[88%] bg-gray-800 rounded-2xl rounded-bl-sm px-3 py-1.5 border border-white/5">
                      <p className="text-white text-[11px] leading-relaxed">That one's our current deal at $1,299. Want me to text you a photo of it?</p>
                    </div>
                  </div>
                )}
                {beat >= 5 && (
                  <div className="hero-pop">
                    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/30 p-2 flex items-center gap-2.5">
                      <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-slate-900 border border-white/10">
                        <HeroDriveImg id={HERO_LOCATION_IMG_ID} alt="Gray sectional" className="absolute inset-0 w-full h-full object-cover" />
                        <span className="absolute inset-y-0 w-5 bg-white/25 blur-sm" style={{ animation: 'heroShine 1.6s ease-in-out 0.3s 2' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-[10px] font-semibold">Photo texted to caller</p>
                        <p className="text-gray-400 text-[8.5px]">Sent mid-call, straight from your catalog</p>
                      </div>
                      <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                    </div>
                  </div>
                )}
                {beat >= 6 && (
                  <div className="animate-slideUp">
                    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/30 p-2.5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <FileText className="w-3 h-3 text-blue-300 flex-shrink-0" />
                        <p className="text-white text-[10.5px] font-bold flex-1">Note left for your floor team</p>
                        <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
                      </div>
                      <p className="text-gray-300 text-[9px] leading-relaxed italic">"Pre-screened: wants the gray L-sectional · ~$1,300 budget · visiting Saturday AM"</p>
                      <div className="flex items-center gap-1.5 pt-1.5 mt-1.5 border-t border-white/10">
                        <TrendingUp className="w-3 h-3 text-green-400 flex-shrink-0" />
                        <p className="text-gray-400 text-[8.5px]">New lead saved · name + number · follow-up queued</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ---- CALL B: existing customer, receipt lookup ---- */}
            {!isCallA && (
              <>
                {beat >= 9 && beat < 13 && (
                  <div className="flex justify-start animate-slideUp">
                    <div className="max-w-[85%] bg-gray-800 rounded-2xl rounded-bl-sm px-3 py-1.5 border border-white/5">
                      <p className="text-white text-[11px] leading-relaxed">Hi Maria! Thanks for calling. How can I help today?</p>
                    </div>
                  </div>
                )}
                {beat >= 10 && beat < 14 && (
                  <div className="flex justify-end animate-slideUp">
                    <div className="max-w-[85%] bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl rounded-br-sm px-3 py-1.5 shadow-lg">
                      <p className="text-white text-[11px]">Just checking on my sectional delivery.</p>
                    </div>
                  </div>
                )}
                {beat === 11 && (
                  <div className="flex justify-start animate-slideUp">
                    <div className="bg-gray-800/80 rounded-2xl rounded-bl-sm px-3 py-1.5 border border-blue-500/20 flex items-center gap-2">
                      <Sparkles className="w-3 h-3 text-blue-300 animate-spin" style={{ animationDuration: '1.5s' }} />
                      <span className="text-blue-200 text-[10px]">Searching her receipts</span>
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" />
                        <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                        <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                      </span>
                    </div>
                  </div>
                )}
                {beat >= 12 && (
                  <div className="hero-pop">
                    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/40 p-2.5 shadow-xl shadow-blue-500/10">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                          <Receipt className="w-3.5 h-3.5 text-blue-300" />
                        </div>
                        <p className="text-white text-[11px] font-bold flex-1">Receipt #4127</p>
                        <span className="px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded text-[7px] font-bold uppercase tracking-wide">Match</span>
                      </div>
                      <p className="text-gray-300 text-[10px] mb-1.5">Gray L-Sectional · $1,299 · paid in full</p>
                      <div className="flex items-center gap-1.5 pt-1.5 border-t border-white/10">
                        <Truck className="w-3 h-3 text-green-400 flex-shrink-0" />
                        <p className="text-green-300 text-[10px] font-semibold">Out for delivery · Thu 2-4 PM</p>
                      </div>
                    </div>
                  </div>
                )}
                {beat >= 13 && (
                  <div className="flex justify-start animate-slideUp">
                    <div className="max-w-[88%] bg-gray-800 rounded-2xl rounded-bl-sm px-3 py-1.5 border border-white/5">
                      <p className="text-white text-[11px] leading-relaxed">Found it! Your sectional arrives Thursday, 2-4 PM. I'll text you the details right now.</p>
                    </div>
                  </div>
                )}
                {beat >= 14 && (
                  <div className="hero-pop">
                    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/30 p-2 flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <Send className="w-3 h-3 text-blue-400" />
                      </div>
                      <p className="text-white text-[10px] font-medium flex-1">Delivery details texted to Maria</p>
                      <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                    </div>
                  </div>
                )}
                {beat >= 15 && (
                  <div className="animate-slideUp">
                    <div className="bg-gradient-to-r from-green-500/15 to-green-500/5 rounded-xl border border-green-500/25 p-2.5 flex items-center gap-2.5">
                      <CheckCircle className="w-4.5 h-4.5 text-green-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-white text-[11px] font-semibold">Handled start to finish. Zero staff time</p>
                        <p className="text-gray-400 text-[9px]">New shopper closed · existing customer cared for</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ============ ACT 3 · REVIEW AUTOPILOT ============ */}
      {scene === 'review' && (
        <div className="h-full flex flex-col animate-fadeIn">
          <div className="px-3.5 py-2.5 border-b border-blue-500/25 bg-gradient-to-r from-blue-500/15 to-blue-600/10 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Star className="w-4.5 h-4.5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-[13px] leading-tight">Review Autopilot</p>
                <p className="text-blue-300/80 text-[9px]">
                  {beat === 0 ? 'Survey texted after every order'
                    : beat === 1 ? 'Maria rates her experience'
                    : beat === 2 ? 'Public review posted'
                    : 'Your rating keeps climbing'}
                </p>
              </div>
              {beat >= 2 && (
                <span className="px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded text-[8px] font-bold uppercase tracking-wide flex-shrink-0">5.0 ★</span>
              )}
            </div>
          </div>

          <div className="flex-1 px-3 pt-2.5 pb-10 space-y-2 min-h-0">
            <div className="animate-fadeIn">
              <p className="text-gray-400 text-[8px] uppercase tracking-widest mb-1">Auto-survey · SMS</p>
              <div className="flex justify-start">
                <div className="max-w-[85%] bg-gray-800 rounded-2xl rounded-bl-sm px-3 py-1.5 border border-white/5">
                  <p className="text-white text-[11px] leading-relaxed">How do you like your new furniture, Maria? Tap a star ⭐</p>
                </div>
              </div>
            </div>

            {beat >= 1 && (
              <div className="hero-pop">
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/30 p-2.5 flex items-center gap-2.5">
                  <div className="flex items-center gap-1 flex-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star
                        key={i}
                        className="w-4 h-4 text-yellow-400 fill-yellow-400 opacity-0"
                        style={{ animation: 'heroPop 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.2) forwards', animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </div>
                  <p className="text-white text-[10px] font-semibold flex-shrink-0">Maria rated 5 stars</p>
                </div>
              </div>
            )}

            {beat >= 2 && (
              <div className="hero-pop">
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/30 p-2.5">
                  <div className="flex items-center gap-1 mb-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" />
                    ))}
                    <span className="text-gray-400 text-[8px] ml-1">Maria L. · Google review</span>
                    <Check className="w-3 h-3 text-green-400 ml-auto flex-shrink-0" />
                  </div>
                  <p className="text-white/90 text-[10px] italic leading-relaxed">"Crew was amazing, love my sectional!"</p>
                </div>
              </div>
            )}

            {beat >= 3 && (
              <div className="animate-slideUp">
                <div className="bg-gradient-to-r from-green-500/15 to-green-500/5 rounded-xl border border-green-500/25 p-2.5 flex items-center gap-2.5">
                  <TrendingUp className="w-4.5 h-4.5 text-green-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-[11px] font-semibold">The full loop, on autopilot</p>
                    <p className="text-gray-400 text-[9px]">Call → sale → care → 5-star review</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-white text-[12px] font-black leading-none">4.9 ★</p>
                    <p className="text-green-300 text-[8px] font-semibold">128 reviews · +1</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ CHAPTER RAIL ============ */}
      {scene !== 'boot' && (
        <div className="absolute bottom-0 inset-x-0 z-20 px-3 pb-1.5 pt-7 bg-gradient-to-t from-gray-950 via-gray-950/75 to-transparent pointer-events-none">
          <div className="flex items-center gap-1.5">
            <div className="flex-1">
              <div className="h-[3px] bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-[width] duration-200 ease-linear ${isCallA ? 'bg-blue-400 shadow-sm shadow-blue-400/60' : 'bg-blue-400/50'}`}
                  style={{ width: `${rail.calla * 100}%` }}
                />
              </div>
              <p className={`text-[7px] font-bold uppercase tracking-widest mt-1 ${isCallA ? 'text-blue-300' : 'text-gray-600'}`}>1 · Sales</p>
            </div>
            <div className="flex-1">
              <div className="h-[3px] bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-[width] duration-200 ease-linear ${scene === 'agent' && !isCallA ? 'bg-blue-400 shadow-sm shadow-blue-400/60' : 'bg-blue-400/50'}`}
                  style={{ width: `${rail.callb * 100}%` }}
                />
              </div>
              <p className={`text-[7px] font-bold uppercase tracking-widest mt-1 ${scene === 'agent' && !isCallA ? 'text-blue-300' : 'text-gray-600'}`}>2 · Care</p>
            </div>
            <div className="flex-1">
              <div className="h-[3px] bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-[width] duration-200 ease-linear ${scene === 'review' ? 'bg-blue-400 shadow-sm shadow-blue-400/60' : 'bg-blue-400/50'}`}
                  style={{ width: `${rail.after * 100}%` }}
                />
              </div>
              <p className={`text-[7px] font-bold uppercase tracking-widest mt-1 ${scene === 'review' ? 'text-blue-300' : 'text-gray-600'}`}>3 · Reviews</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// PHONE MOCKUP COMPONENT
// ============================================
// The four storyboard demos are all composed for the desktop screen
// (288x580 frame at sm, 320x680 at lg). The old mobile treatment squashed
// that composition into a shorter 288x440 frame, then shrank the result to
// 75% - flex sections crushed together and every 9-11px caption rendered
// at ~7px. That was the smoosh.
//
// New mobile treatment: keep the exact 288x580 composition the demos were
// choreographed for and scale it UP to fill the viewport width (capped at
// 1.28x). Identical layout, identical timing, ~25% larger type - the
// visitor's own phone becomes the phone. sm+ is untouched.
// The demos were composed inside a 288x580 frame. The old mobile treatment
// kept that exact frame and CSS-transformed it, which meant the mockup could
// never get WIDER than the composition - it could only get taller. Worse, the
// measuring div was `w-full` inside a shrink-to-fit flex item, so it measured
// its own content (288px) instead of the column, settled at ~0.97 scale, and
// sat visually off-centre once the glow overflowed to one side.
//
// New treatment below lg: the frame is SIZED, not scaled. Width grows to the
// real measured column width (up to 380px), which is where the extra room for
// captions and button labels comes from - the internal layout is flex-based,
// so more width means less wrapping and nothing running off a pill. Height is
// either the natural 580 or, when `fit` is set (the hero), whatever vertical
// space the flex parent hands it, so the whole hero lands on one screen.
const PHONE_STAGE_W = 288; // the width every demo was originally composed for
const PHONE_STAGE_H = 580;
const MOBILE_MIN_W = 288;
const MOBILE_MAX_W = 330;  // wider than this stops reading as a phone
const MOBILE_MIN_H = 430;  // below this the demos genuinely do smoosh
const MOBILE_ASPECT = 0.56; // width never exceeds height * this - stays phone-shaped

function usePhoneStage({ fit = false } = {}) {
  // Size comes from the measured host box, never window.innerWidth - zoomed,
  // text-boosted and desktop-mode viewports can't lie about real space. The
  // host must be a genuine full-width block (see the `w-full` wrappers at the
  // call sites) or it measures its own content and starves the frame.
  const hostRef = useRef(null);
  const [box, setBox] = useState(() => ({
    w: PHONE_STAGE_W,
    h: PHONE_STAGE_H,
    compact: typeof window !== 'undefined' && window.innerWidth < 1024,
  }));

  useEffect(() => {
    const compute = () => {
      if (window.innerWidth >= 1024) {
        setBox({ w: 320, h: 680, compact: false });
        return;
      }
      const host = hostRef.current;
      const availW = host && host.offsetWidth ? host.offsetWidth : PHONE_STAGE_W;
      const availH = host ? host.offsetHeight : 0;

      // Height first: the hero hands us a bounded box, the story sections don't.
      let h = PHONE_STAGE_H;
      if (fit && availH) {
        h = Math.round(Math.max(MOBILE_MIN_H, Math.min(PHONE_STAGE_H, availH)));
      }
      // Then the widest frame that fits the column and still reads as a phone.
      const w = Math.round(
        Math.max(MOBILE_MIN_W, Math.min(MOBILE_MAX_W, availW, h * MOBILE_ASPECT))
      );

      setBox((prev) => (prev.w === w && prev.h === h && prev.compact ? prev : { w, h, compact: true }));
    };

    compute();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(compute) : null;
    if (ro && hostRef.current) ro.observe(hostRef.current);
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
    };
  }, [fit]);

  return { hostRef, ...box };
}

function PhoneMockup({ children, glowColor = 'from-blue-500/12 to-blue-500/12', caption, fit = false }) {
  const { hostRef, w, h, compact } = usePhoneStage({ fit });

  // Below lg the frame is explicitly sized; at lg+ the original classes win.
  const frameStyle = compact ? { width: w, height: h } : undefined;
  // Caption sits above the frame on mobile and is measured out of the fit box.
  const captionEl = caption && compact ? (
    <div className="mb-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-200">
      <span className="w-1.5 h-1.5 rounded-full bg-blue-400" aria-hidden="true" />
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600">{caption}</span>
    </div>
  ) : null;

  return (
    <div className={`w-full min-w-0 flex flex-col items-center justify-center ${fit ? 'h-full min-h-0' : ''}`}>
      {captionEl}
      {/* The measured box. With `fit` it is the flex-1 remainder under the
          caption, so the frame never inherits the caption's height and
          overflows into the copy above. */}
      <div
        ref={hostRef}
        className={`w-full min-w-0 flex items-center justify-center ${fit ? 'flex-1 min-h-0' : ''}`}
      >
      <div className="relative" style={{ WebkitTextSizeAdjust: '100%', textSizeAdjust: '100%' }}>
        {/* Phone glow */}
        <div className={`absolute -inset-4 lg:-inset-6 bg-gradient-to-r ${glowColor} rounded-[2.5rem] lg:rounded-[3.5rem] blur-xl lg:blur-2xl`} />

        {/* Phone frame */}
        <div
          className={`relative mx-auto ${compact ? '' : 'w-72 lg:w-80 h-[580px] lg:h-[680px]'} bg-gradient-to-b from-gray-800 to-gray-900 rounded-[2.5rem] lg:rounded-[3rem] p-1.5 lg:p-2 shadow-2xl border border-gray-700`}
          style={frameStyle}
        >
          {/* Screen */}
          <div className="w-full h-full bg-gray-950 rounded-[2rem] lg:rounded-[2.5rem] overflow-hidden relative">
            {/* Notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 lg:w-32 h-5 lg:h-7 bg-black rounded-b-xl lg:rounded-b-2xl z-20" />

            {/* Status bar */}
            <div className="absolute top-0 left-0 right-0 h-10 lg:h-12 bg-gray-900/80 backdrop-blur-sm z-10 flex items-end justify-between px-6 lg:px-8 pb-1">
              <span className="text-white text-[10px] lg:text-xs font-medium">9:41</span>
              <div className="flex items-end space-x-1">
                <div className="flex items-end space-x-0.5">
                  <div className="w-0.5 lg:w-1 h-1 bg-white rounded-full" />
                  <div className="w-0.5 lg:w-1 h-1.5 lg:h-2 bg-white rounded-full" />
                  <div className="w-0.5 lg:w-1 h-2 lg:h-3 bg-white rounded-full" />
                  <div className="w-0.5 lg:w-1 h-2.5 lg:h-4 bg-white rounded-full" />
                </div>
                <div className="w-4 lg:w-5 h-2 lg:h-2.5 border border-white rounded-sm ml-1">
                  <div className="w-3 lg:w-4 h-full bg-green-500 rounded-sm" />
                </div>
              </div>
            </div>

            {/* Content area */}
            <div className="absolute inset-0 top-12 lg:top-14 bottom-2 overflow-hidden">
              {children}
            </div>

            {/* Home indicator */}
            <div className="absolute bottom-1.5 lg:bottom-2 left-1/2 -translate-x-1/2 w-24 lg:w-32 h-1 bg-white/30 rounded-full" />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

// ============================================
// STEP 2: DIGITAL RECEIPTS - THE LIVING RECEIPT
//   Chapter 1 · Store side: build the receipt in seconds,
//               one tap texts it to the customer via SMS.
//   Chapter 2 · Customer's thread: the receipt arrives - then
//               the store updates the order and their copy
//               updates LIVE. No reprint, no new link.
//   Chapter 3 · Same thread: the customer chats with their
//               receipt, and the AI answers from what's on it.
// ============================================
function ReceiptPhoneContent({ isActive }) {
  const [beat, setBeat] = useState(0);
  const startedRef = useRef(false);
  const timersRef = useRef([]);
  const scrollRef = useRef(null);

  // Plays once when it scrolls into view and holds on the payoff (Phillip,
  // 2026-10-29: the animations were too much). The hero still loops.
  useEffect(() => {
    if (!isActive || startedRef.current) return;
    startedRef.current = true;

    const TIMELINE = [
      // ---- CHAPTER 1 · store side: build & send ----
      { t: 0, b: 0 },      // empty new-receipt form
      { t: 700, b: 1 },    // customer added
      { t: 1600, b: 2 },   // items drop in
      { t: 2600, b: 3 },   // total ready
      { t: 3500, b: 4 },   // one tap - texted
      // ---- CHAPTER 2 · customer's thread: the living receipt ----
      { t: 5300, b: 5 },   // receipt lands in the SMS thread
      { t: 8300, b: 6 },   // store updates the order → receipt updates LIVE
      // ---- CHAPTER 3 · chat with your receipt ----
      { t: 11000, b: 7 },  // customer asks, right in the thread
      { t: 12200, b: 8 },  // AI reading the receipt…
      { t: 13400, b: 9 },  // answered from the receipt
      { t: 15600, b: 10 }, // payoff: no anxious "is it ready?" calls
    ];

    const clearAll = () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };
    const run = () => {
      clearAll();
      TIMELINE.forEach(({ t, b }) => timersRef.current.push(setTimeout(() => setBeat(b), t)));
    };
    run();

    return undefined;
  }, [isActive]);

  useEffect(() => () => { timersRef.current.forEach(clearTimeout); }, []);

  // Auto-scroll the SMS thread as it grows
  useEffect(() => {
    if (beat >= 5 && scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [beat]);

  const storeSide = beat < 5;

  return (
    <div className="h-full flex flex-col bg-gray-950 relative overflow-hidden">
      <style>{`
        @keyframes rcPop { 0% { opacity: 0; transform: scale(0.92) translateY(6px); } 100% { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes rcDrop {
          0% { opacity: 0; transform: translateY(-22px) scale(1.04); }
          70% { opacity: 1; transform: translateY(2px) scale(0.99); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .rc-pop { animation: rcPop 0.4s cubic-bezier(0.2, 0.9, 0.3, 1.2) forwards; }
        @media (prefers-reduced-motion: reduce) {
          .rc-scene *, .rc-scene *::before, .rc-scene *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <div className="rc-scene h-full flex flex-col">

        {/* ====== CHAPTER 1 · STORE SIDE: BUILD & TEXT IT ====== */}
        {storeSide && (
          <div className="h-full flex flex-col animate-fadeIn">
            <div className="px-3.5 py-2.5 border-b border-blue-500/25 bg-gradient-to-r from-blue-500/15 to-blue-600/10 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-4.5 h-4.5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-[13px] leading-tight">Digital Receipts</p>
                  <p className="text-blue-300/80 text-[9px]">
                    {beat < 4 ? 'New receipt. Takes seconds' : 'Texted. SMS thread open.'}
                  </p>
                </div>
                {beat >= 4 && (
                  <span className="px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded text-[8px] font-bold uppercase tracking-wide flex-shrink-0 rc-pop">Sent</span>
                )}
              </div>
            </div>

            <div className="flex-1 px-3 pt-2.5 pb-3 min-h-0">
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-white/10 p-2.5 space-y-2">

                {/* Customer */}
                {beat >= 1 ? (
                  <div className="flex items-center gap-2" style={{ animation: 'rcDrop 0.5s cubic-bezier(0.2, 0.9, 0.3, 1.2) forwards' }}>
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-[9px] font-bold">MR</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-[11px] font-bold leading-tight">Maria R.</p>
                      <p className="text-gray-400 text-[8.5px] font-mono">(209) 555-0148</p>
                    </div>
                    <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
                  </div>
                ) : (
                  <div className="rounded-lg h-9 border-2 border-dashed border-white/15 flex items-center justify-center gap-1.5 animate-pulse">
                    <User className="w-3 h-3 text-gray-600" />
                    <span className="text-gray-600 text-[8.5px]">Add customer</span>
                  </div>
                )}

                {/* Items */}
                {beat >= 2 ? (
                  <div className="space-y-1.5 pt-2 border-t border-white/10" style={{ animation: 'rcDrop 0.5s cubic-bezier(0.2, 0.9, 0.3, 1.2) forwards' }}>
                    <div className="flex items-center gap-2">
                      <div className="relative w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-slate-900">
                        <HeroDriveImg id={HERO_LOCATION_IMG_ID} alt="Gray sectional" className="absolute inset-0 w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-[10px] font-semibold truncate">Sectional Sofa</p>
                        <p className="text-gray-500 text-[8px]">Gray Fabric · Qty 1</p>
                      </div>
                      <p className="text-white text-[10.5px] font-bold flex-shrink-0">$1,299</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                        <Box className="w-3.5 h-3.5 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-[10px] font-semibold truncate">Coffee Table</p>
                        <p className="text-gray-500 text-[8px]">Natural Oak · Qty 1</p>
                      </div>
                      <p className="text-white text-[10.5px] font-bold flex-shrink-0">$349</p>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg h-9 border-2 border-dashed border-white/15 flex items-center justify-center gap-1.5">
                    <Box className="w-3 h-3 text-gray-600" />
                    <span className="text-gray-600 text-[8.5px]">Add items</span>
                  </div>
                )}

                {/* Total */}
                {beat >= 3 && (
                  <div className="rc-pop flex items-center justify-between pt-2 border-t border-white/10">
                    <div>
                      <p className="text-white text-[10.5px] font-bold">Total</p>
                      <p className="text-gray-500 text-[7.5px]">Tax included · Free delivery</p>
                    </div>
                    <span className="text-[15px] font-black bg-gradient-to-r from-blue-400 to-blue-500 bg-clip-text text-transparent">$1,783.96</span>
                  </div>
                )}
              </div>

              {/* One tap - texted */}
              {beat >= 3 && beat < 4 && (
                <div className="rc-pop mt-2.5">
                  <div className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-blue-500/30">
                    <Send className="w-3.5 h-3.5 text-white" />
                    <span className="text-white text-[11px] font-bold">Text receipt to Maria</span>
                  </div>
                </div>
              )}
              {beat >= 4 && (
                <div className="rc-pop mt-2.5">
                  <div className="bg-gradient-to-r from-green-500/15 to-green-500/5 rounded-xl border border-green-500/25 p-2.5 flex items-center gap-2.5">
                    <CheckCircle className="w-4.5 h-4.5 text-green-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white text-[11px] font-semibold">Receipt texted in one tap</p>
                      <p className="text-gray-400 text-[9px]">No paper, no printer · SMS thread open</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ====== CHAPTERS 2-3 · THE CUSTOMER'S SMS THREAD ====== */}
        {!storeSide && (
          <div className="h-full flex flex-col animate-fadeIn">
            <div className="px-3.5 py-2.5 border-b border-white/10 flex-shrink-0 flex items-center gap-2.5">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-[11px] font-bold">YS</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-[13px] leading-tight">Your Store</p>
                <p className="text-green-400 text-[9px] flex items-center">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1 animate-pulse" />
                  Business · Verified
                </p>
              </div>
              <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded text-[8px] font-bold uppercase tracking-wide flex-shrink-0">Maria's phone</span>
            </div>

            <div ref={scrollRef} className="flex-1 px-3 pt-2.5 pb-3 space-y-2 overflow-auto scroll-smooth min-h-0">

              {/* The living receipt, right in the thread */}
              {beat >= 5 && (
                <div className="rc-pop">
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-white/15 overflow-hidden shadow-xl shadow-blue-500/10">
                    <div className="bg-gradient-to-r from-blue-500/25 to-blue-600/15 px-2.5 py-1.5 border-b border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Receipt className="w-3 h-3 text-blue-300" />
                        <span className="text-white text-[10px] font-bold">Order #4829</span>
                      </div>
                      <span className="px-1.5 py-0.5 bg-green-500/25 text-green-300 rounded-full text-[7px] font-bold">PAID ✓</span>
                    </div>
                    <div className="p-2 space-y-1.5">
                      {/* Each item carries its own live status */}
                      <div className="flex items-center gap-2">
                        <div className="relative w-7 h-7 rounded-md overflow-hidden flex-shrink-0 bg-slate-900">
                          <HeroDriveImg id={HERO_LOCATION_IMG_ID} alt="Gray sectional" className="absolute inset-0 w-full h-full object-cover" />
                        </div>
                        <p className="text-white text-[9px] font-medium flex-1 min-w-0 truncate">Sectional Sofa</p>
                        {beat < 6 ? (
                          <span className="text-yellow-300 text-[7px] font-bold uppercase tracking-wide flex-shrink-0">Preparing</span>
                        ) : (
                          <span className="rc-pop flex items-center gap-0.5 flex-shrink-0">
                            <Check className="w-3 h-3 text-green-400" />
                            <span className="text-green-300 text-[7px] font-bold uppercase tracking-wide">Ready</span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                          <Box className="w-3 h-3 text-blue-400" />
                        </div>
                        <p className="text-white text-[9px] font-medium flex-1 min-w-0 truncate">Coffee Table</p>
                        {beat < 6 ? (
                          <span className="text-yellow-300 text-[7px] font-bold uppercase tracking-wide flex-shrink-0">Preparing</span>
                        ) : (
                          <span className="flex items-center gap-0.5 flex-shrink-0 opacity-0" style={{ animation: 'rcPop 0.4s cubic-bezier(0.2, 0.9, 0.3, 1.2) 0.45s forwards' }}>
                            <Check className="w-3 h-3 text-green-400" />
                            <span className="text-green-300 text-[7px] font-bold uppercase tracking-wide">Ready</span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between pt-1 border-t border-white/10">
                        <p className="text-gray-400 text-[8px]">Total</p>
                        <p className="text-white text-[9.5px] font-bold">$1,783.96</p>
                      </div>

                      {/* Order status - this is the LIVING part */}
                      {beat < 6 ? (
                        <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/25 rounded-lg px-2 py-1.5">
                          <Clock className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                          <span className="text-yellow-300 text-[8.5px] font-semibold flex-1">Preparing your order</span>
                          <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse flex-shrink-0" />
                        </div>
                      ) : (
                        <div className="rc-pop flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-lg px-2 py-1.5">
                          <Truck className="w-3 h-3 text-green-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-green-300 text-[8.5px] font-semibold leading-tight">Ready · delivery scheduled</p>
                            <p className="text-gray-400 text-[7.5px]">Wed, Jan 8 · 9:00-12:00</p>
                          </div>
                          <span className="px-1 py-0.5 bg-green-500/25 text-green-300 rounded text-[6.5px] font-bold uppercase tracking-wide animate-pulse flex-shrink-0">Live</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* The update came from the store side */}
              {beat >= 6 && (
                <div className="rc-pop flex justify-center">
                  <div className="px-2 py-1 bg-white/5 border border-white/10 rounded-full flex items-center gap-1.5">
                    <Sparkles className="w-2.5 h-2.5 text-blue-300 flex-shrink-0" />
                    <span className="text-gray-300 text-[7.5px]">Store marked the items ready. Maria watched it change live</span>
                  </div>
                </div>
              )}

              {/* Chapter 3 - chat with your receipt */}
              {beat >= 7 && (
                <div className="flex justify-end animate-slideUp">
                  <div className="max-w-[85%] bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl rounded-br-sm px-3 py-1.5 shadow-lg">
                    <p className="text-white text-[11px]">Hi! Is my order ready yet? 😅</p>
                  </div>
                </div>
              )}

              {beat === 8 && (
                <div className="flex justify-start animate-slideUp">
                  <div className="bg-gray-800/80 rounded-2xl rounded-bl-sm px-3 py-1.5 border border-blue-500/20 flex items-center gap-2">
                    <Bot className="w-3 h-3 text-blue-300" />
                    <span className="text-blue-200 text-[10px]">Reading your receipt</span>
                    <span className="flex gap-0.5">
                      <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" />
                      <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                      <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                    </span>
                  </div>
                </div>
              )}

              {beat >= 9 && (
                <div className="flex justify-start animate-slideUp">
                  <div className="max-w-[88%] space-y-1">
                    <div className="bg-gray-800 rounded-2xl rounded-bl-sm px-3 py-1.5 border border-white/5">
                      <p className="text-white text-[11px] leading-relaxed">Yes, Maria! Both items are ready! Delivery is set for Wed, Jan 8, 9:00-12:00. It's all on your receipt above 👆</p>
                    </div>
                    <div className="flex items-center gap-1 pl-1">
                      <Sparkles className="w-2.5 h-2.5 text-blue-400 flex-shrink-0" />
                      <span className="text-blue-300/80 text-[7.5px] font-semibold">AI · answered from the receipt</span>
                    </div>
                  </div>
                </div>
              )}

              {beat >= 10 && (
                <div className="animate-slideUp">
                  <div className="bg-gradient-to-r from-green-500/15 to-green-500/5 rounded-xl border border-green-500/25 p-2.5 flex items-center gap-2.5">
                    <CheckCircle className="w-4.5 h-4.5 text-green-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white text-[11px] font-semibold">No more "is it ready yet?" calls</p>
                      <p className="text-gray-400 text-[9px]">Customers ask the receipt · the AI answers, 24/7</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// STEP 1: AI PHONE AGENT × PRODUCT CATALOG
// Deeper cut of the hero story for this section:
//   Chapter 1 · Create the deal in the app - AI designs the flyer
//               and arms the agent on every channel. No designer.
//   Chapter 2 · A caller asks about the deal - the agent leads
//               with it, quotes it, texts the exact picture, and
//               books the store visit.
// A Flyer → Catalog → AI Agent pipeline stays pinned at the
// bottom, with a pulse traveling along it at each moment.
// ============================================

// One sectional across the whole story:
// catalog → flyer → call → picture texted.
const PA_PRODUCT_IMG_ID = HERO_LOCATION_IMG_ID;

function PAPipeline({ beat }) {
  const flyerLit = beat >= 4;
  const catalogLit = beat >= 1;
  const agentLit = beat >= 5;
  const done = beat >= 13;
  const stage = beat === 4 ? 'flyer' : beat === 5 ? 'sync' : beat === 8 ? 'lookup' : done ? 'done' : 'idle';

  const Node = ({ icon: Icon, label, lit }) => (
    <div className="flex flex-col items-center flex-shrink-0">
      <div className={`relative w-7 h-7 rounded-full flex items-center justify-center border transition-all duration-500 ${lit ? 'bg-blue-500/20 border-blue-400/60 shadow-md shadow-blue-500/30' : 'bg-white/5 border-white/15'}`}>
        <Icon className={`w-3 h-3 transition-colors duration-500 ${lit ? 'text-blue-300' : 'text-gray-500'}`} />
        {done && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full flex items-center justify-center border border-gray-950">
            <Check className="w-1.5 h-1.5 text-white" />
          </span>
        )}
      </div>
      <span className={`text-[6.5px] font-bold uppercase tracking-widest mt-1 transition-colors duration-500 ${lit ? 'text-blue-300' : 'text-gray-600'}`}>{label}</span>
    </div>
  );

  const Connector = ({ active, reverse }) => (
    <div className="flex-1 relative h-px mx-1.5 -mt-3.5 bg-white/15 overflow-visible">
      {active && (
        <>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/40 via-blue-400/60 to-blue-500/40" />
          <span
            className="absolute -top-[2.5px] w-1.5 h-1.5 rounded-full bg-blue-300 shadow-md shadow-blue-400/80"
            style={{ animation: `paTravel 0.9s linear infinite ${reverse ? 'reverse' : 'normal'}` }}
          />
        </>
      )}
      {!active && stage === 'done' && <div className="absolute inset-0 bg-blue-400/50" />}
    </div>
  );

  return (
    <div className="absolute bottom-0 inset-x-0 z-20 px-4 pb-2 pt-7 bg-gradient-to-t from-gray-950 via-gray-950/80 to-transparent pointer-events-none">
      <div className="flex items-center justify-between">
        <Node icon={Sparkles} label="Flyer" lit={flyerLit} />
        <Connector active={stage === 'flyer'} reverse={false} />
        <Node icon={Box} label="Catalog" lit={catalogLit} />
        <Connector active={stage === 'sync' || stage === 'lookup'} reverse={stage === 'lookup'} />
        <Node icon={Phone} label="AI Agent" lit={agentLit} />
      </div>
    </div>
  );
}

function PhoneAgentContent({ isActive }) {
  const [beat, setBeat] = useState(0);
  const [callSec, setCallSec] = useState(0);
  const startedRef = useRef(false);
  const timersRef = useRef([]);
  const clockRef = useRef(null);
  const loopStartRef = useRef(Date.now());

  const CALL_ANSWER = 7600;
  const CALL_END = 16400;

  useEffect(() => {
    if (!isActive || startedRef.current) return;
    startedRef.current = true;

    const TIMELINE = [
      // ---- CHAPTER 1 · create the deal in the app ----
      { t: 0, b: 0 },      // empty "new product" card
      { t: 700, b: 1 },    // photo drops in
      { t: 1500, b: 2 },   // name + item number
      { t: 2300, b: 3 },   // price + deal tag
      { t: 3200, b: 4 },   // flyer designed by AI - no designer needed
      { t: 4500, b: 5 },   // live on your AI agent
      { t: 5700, b: 6 },   // armed on every channel: phone, SMS, web
      // ---- CHAPTER 2 · a caller asking about the deal ----
      { t: 7600, b: 7 },   // live call - "saw your flyer"
      { t: 9200, b: 8 },   // agent checks the catalog
      { t: 10300, b: 9 },  // current deal retrieved - exact match
      { t: 11700, b: 10 }, // agent leads with the deal + price
      { t: 13600, b: 11 }, // the exact picture, texted on a yes
      { t: 15000, b: 12 }, // store visit booked
      { t: 16400, b: 13 }, // the payoff: create once, sold on every call
    ];

    const clearAll = () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };
    const run = () => {
      clearAll();
      loopStartRef.current = Date.now();
      TIMELINE.forEach(({ t, b }) => timersRef.current.push(setTimeout(() => setBeat(b), t)));
    };
    run();

    clockRef.current = setInterval(() => {
      const e = Date.now() - loopStartRef.current;
      if (e >= CALL_ANSWER) {
        setCallSec(Math.floor((Math.min(e, CALL_END) - CALL_ANSWER) / 1000));
      } else {
        setCallSec(0);
      }
    }, 150);

    return undefined;
  }, [isActive]);

  useEffect(() => () => {
    timersRef.current.forEach(clearTimeout);
    if (clockRef.current) clearInterval(clockRef.current);
  }, []);

  const fmtSec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const inCall = beat >= 7;
  const callOver = beat >= 13;

  return (
    <div className="h-full flex flex-col bg-gray-950 relative overflow-hidden">
      <style>{`
        @keyframes paTravel { 0% { left: 0%; } 100% { left: calc(100% - 6px); } }
        @keyframes paPop { 0% { opacity: 0; transform: scale(0.92) translateY(6px); } 100% { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes paDrop {
          0% { opacity: 0; transform: translateY(-26px) scale(1.05); }
          70% { opacity: 1; transform: translateY(2px) scale(0.99); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes paWave { 0% { transform: scaleY(0.35); } 100% { transform: scaleY(1); } }
        .pa-pop { animation: paPop 0.4s cubic-bezier(0.2, 0.9, 0.3, 1.2) forwards; }
        @media (prefers-reduced-motion: reduce) {
          .pa-scene *, .pa-scene *::before, .pa-scene *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <div className="pa-scene h-full flex flex-col">

        {/* ====== CHAPTER 1 · CREATE THE DEAL ====== */}
        {!inCall && (
          <div className="h-full flex flex-col animate-fadeIn">
            <div className="px-3.5 py-2.5 border-b border-blue-500/25 bg-gradient-to-r from-blue-500/15 to-blue-600/10 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Box className="w-4.5 h-4.5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-[13px] leading-tight">Product Catalog</p>
                  <p className="text-blue-300/80 text-[9px]">
                    {beat < 4 ? 'Creating a new deal'
                      : beat < 5 ? 'AI designed the flyer. No designer'
                      : beat < 6 ? 'Live on your AI agent'
                      : 'Armed on phone, SMS & web'}
                  </p>
                </div>
                {beat >= 5 && (
                  <span className="px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded text-[8px] font-bold uppercase tracking-wide flex-shrink-0 pa-pop">Live</span>
                )}
              </div>
            </div>

            <div className="flex-1 px-3 pt-2.5 pb-12 min-h-0">
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-white/10 p-2.5 space-y-2">
                {/* Product photo */}
                {beat >= 1 ? (
                  <div className="relative rounded-lg overflow-hidden h-20" style={{ animation: 'paDrop 0.55s cubic-bezier(0.2, 0.9, 0.3, 1.2) forwards' }}>
                    <HeroDriveImg id={PA_PRODUCT_IMG_ID} alt="Gray sectional" className="absolute inset-0 w-full h-full object-cover" />
                    {beat >= 3 && (
                      <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-amber-400 text-slate-900 rounded-full text-[7px] font-black uppercase tracking-wide pa-pop">Current deal</span>
                    )}
                  </div>
                ) : (
                  <div className="rounded-lg h-20 border-2 border-dashed border-white/15 flex items-center justify-center animate-pulse">
                    <Camera className="w-4 h-4 text-gray-600" />
                  </div>
                )}

                {/* Name + item number */}
                {beat >= 2 && (
                  <div className="pa-pop flex items-center gap-2">
                    <p className="text-white text-[12px] font-bold flex-1 min-w-0 truncate">Gray L-Sectional</p>
                    <span className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-[8.5px] font-mono text-blue-300 flex-shrink-0">#EXT-4127</span>
                  </div>
                )}

                {/* Price + stock */}
                {beat >= 3 && (
                  <div className="pa-pop flex items-center gap-2">
                    <span className="px-2 py-1 bg-blue-500/15 border border-blue-500/30 rounded-lg text-white text-[11px] font-black">$1,299</span>
                    <span className="px-2 py-1 bg-amber-400/15 border border-amber-400/30 rounded-lg text-amber-300 text-[9.5px] font-semibold flex items-center gap-1">
                      <Sparkles className="w-2.5 h-2.5" /> 40% off
                    </span>
                  </div>
                )}

                {/* Flyer designed by AI */}
                {beat >= 4 && (
                  <div className="pa-pop flex items-center gap-2 pt-2 border-t border-white/10">
                    <div className="relative w-8 h-10 rounded-md overflow-hidden flex-shrink-0 bg-slate-900 border border-blue-400/40 shadow-md shadow-blue-500/20">
                      <HeroDriveImg id={PA_PRODUCT_IMG_ID} alt="Product flyer" className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0.5 text-center">
                        <p className="text-yellow-400 font-black text-[8px] leading-none drop-shadow">40%</p>
                        <p className="text-white font-bold text-[4.5px] leading-none tracking-widest">OFF</p>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-[10px] font-semibold">Flyer designed by AI</p>
                      <p className="text-blue-300/80 text-[8.5px] flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> No designer, no agency
                      </p>
                    </div>
                    <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                  </div>
                )}
              </div>

              {/* Live on the agent */}
              {beat === 5 && (
                <div className="pa-pop mt-2.5">
                  <div className="bg-gradient-to-r from-blue-500/15 to-blue-500/5 rounded-xl border border-blue-500/25 p-2.5 flex items-center gap-2.5">
                    <div className="relative flex-shrink-0">
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Phone className="w-3 h-3 text-blue-300" />
                      </div>
                      <span className="absolute inset-0 rounded-full bg-blue-400/40 animate-ping" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-[11px] font-semibold">Your AI agent now leads with this deal</p>
                      <p className="text-gray-400 text-[9px]">Price · picture · booking · every call</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Armed on every channel, automatically */}
              {beat >= 6 && (
                <div className="pa-pop mt-2.5">
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/30 p-2.5">
                    <p className="text-white text-[11px] font-bold mb-1.5">Armed on every channel, automatically</p>
                    {[
                      { icon: Phone, text: 'Phone · agent leads with it on calls' },
                      { icon: MessageSquare, text: 'SMS · texted when customers ask' },
                      { icon: Globe, text: 'Web · quoted in your site chat' },
                    ].map(({ icon: Icon, text }, i) => (
                      <div key={i} className="flex items-center gap-2 py-0.5 opacity-0" style={{ animation: 'paPop 0.35s ease-out forwards', animationDelay: `${0.15 + i * 0.2}s` }}>
                        <div className="w-4 h-4 rounded-full bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-2.5 h-2.5 text-blue-300" />
                        </div>
                        <p className="text-gray-300 text-[9.5px] flex-1">{text}</p>
                        <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ====== CHAPTER 2 · THE CALL FROM SOCIAL ====== */}
        {inCall && (
          <div className="h-full flex flex-col animate-fadeIn">
            <div className="px-3.5 py-2.5 border-b border-blue-500/25 bg-gradient-to-r from-blue-500/15 to-blue-600/10 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="relative flex-shrink-0">
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  {!callOver && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-gray-950" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-[13px] leading-tight truncate">Live Caller</p>
                  <p className="text-blue-300/80 text-[9px] truncate">Asking about your current deal</p>
                </div>
                {/* The pill IS the waveform - see hero call header. */}
                <span
                  aria-label={callOver ? 'Call ended' : 'Live call'}
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wide flex-shrink-0 tabular-nums ${callOver ? 'bg-white/10 text-gray-300' : 'bg-green-500/20 text-green-300'}`}
                >
                  {!callOver && (
                    <span className="flex items-end gap-[2px] h-2.5" aria-hidden="true">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <span
                          key={i}
                          className="w-[2px] rounded-full bg-green-300"
                          style={{
                            height: `${34 + Math.abs(Math.sin(i * 1.7)) * 58}%`,
                            animation: `paWave 0.65s ease-in-out ${i * 0.08}s infinite alternate`,
                          }}
                        />
                      ))}
                    </span>
                  )}
                  {callOver ? `Ended ${fmtSec(callSec)}` : fmtSec(callSec)}
                </span>
              </div>
            </div>

            <div className="flex-1 px-3 pt-2.5 pb-12 space-y-2 overflow-hidden min-h-0">
              {beat >= 7 && beat < 12 && (
                <div className="flex justify-end animate-slideUp">
                  <div className="max-w-[85%] bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl rounded-br-sm px-3 py-1.5 shadow-lg">
                    <p className="text-white text-[11px]">I saw your flyer for a gray sectional. How much is that one?</p>
                  </div>
                </div>
              )}

              {beat === 8 && (
                <div className="flex justify-start animate-slideUp">
                  <div className="bg-gray-800/80 rounded-2xl rounded-bl-sm px-3 py-1.5 border border-blue-500/20 flex items-center gap-2">
                    <Box className="w-3 h-3 text-blue-300" />
                    <span className="text-blue-200 text-[10px]">Checking your catalog</span>
                    <span className="flex gap-0.5">
                      <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" />
                      <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                      <span className="w-1 h-1 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                    </span>
                  </div>
                </div>
              )}

              {/* The same catalog card, retrieved live */}
              {beat >= 9 && (
                <div className="pa-pop">
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/40 p-2 shadow-xl shadow-blue-500/10 flex items-center gap-2.5">
                    <div className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      <HeroDriveImg id={PA_PRODUCT_IMG_ID} alt="Gray sectional" className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-white text-[10.5px] font-bold truncate">Gray L-Sectional</p>
                        <span className="px-1 py-0.5 bg-amber-400/20 text-amber-300 rounded text-[6.5px] font-bold uppercase tracking-wide flex-shrink-0">Current deal</span>
                      </div>
                      <p className="text-gray-300 text-[9px] font-mono">#EXT-4127 · $1,299 · 40% off</p>
                      <p className="text-blue-300/80 text-[8px] flex items-center gap-1 mt-0.5">
                        <Sparkles className="w-2 h-2" /> Picture attached
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {beat >= 10 && beat < 13 && (
                <div className="flex justify-start animate-slideUp">
                  <div className="max-w-[88%] bg-gray-800 rounded-2xl rounded-bl-sm px-3 py-1.5 border border-white/5">
                    <p className="text-white text-[11px] leading-relaxed">That's our current deal: $1,299, 40% off. Want me to text you the picture?</p>
                  </div>
                </div>
              )}

              {beat >= 11 && (
                <div className="pa-pop">
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/30 p-2 flex items-center gap-2.5">
                    <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-slate-900 border border-white/10">
                      <HeroDriveImg id={PA_PRODUCT_IMG_ID} alt="Gray sectional" className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-[10px] font-semibold">Picture texted to caller</p>
                      <p className="text-gray-400 text-[8.5px]">The same one from your flyer</p>
                    </div>
                    <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                  </div>
                </div>
              )}

              {beat >= 12 && (
                <div className="pa-pop">
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/30 p-2 flex items-center gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-3 h-3 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-[10px] font-semibold">Store visit booked</p>
                      <p className="text-gray-400 text-[8.5px]">Saturday, 11:00 AM · reminder texted</p>
                    </div>
                    <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                  </div>
                </div>
              )}

              {beat >= 13 && (
                <div className="animate-slideUp">
                  <div className="bg-gradient-to-r from-green-500/15 to-green-500/5 rounded-xl border border-green-500/25 p-2.5 flex items-center gap-2.5">
                    <CheckCircle className="w-4.5 h-4.5 text-green-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white text-[11px] font-semibold">Create it once. Sold on every call</p>
                      <p className="text-gray-400 text-[9px]">Quoted · picture texted · visit booked, automatically</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* The connection, always visible */}
        <PAPipeline beat={beat} />
      </div>
    </div>
  );
}

// ============================================
// STEP 3: REVIEW PHONE CONTENT - CINEMATIC 5-STAR EXPERIENCE
// ============================================
function ReviewPhoneContent({ isActive }) {
  const [animationStage, setAnimationStage] = useState(0);
  const [starCount, setStarCount] = useState(0);
  const [reviewCount, setReviewCount] = useState(127);
  const [avgRating, setAvgRating] = useState(4.6);
  const animationStarted = useRef(false);
  const scrollRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current && animationStage > 4) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [animationStage]);

  useEffect(() => {
    if (isActive && !animationStarted.current) {
      animationStarted.current = true;
      
      const sequence = [
        // SMS notification arrives
        { delay: 400, action: () => setAnimationStage(1) },
        // Survey card appears
        { delay: 1500, action: () => setAnimationStage(2) },
        // Stars start filling - dramatic pause between each
        { delay: 2800, action: () => setStarCount(1) },
        { delay: 3300, action: () => setStarCount(2) },
        { delay: 3800, action: () => setStarCount(3) },
        { delay: 4300, action: () => setStarCount(4) },
        { delay: 5000, action: () => setStarCount(5) },
        // 5 star celebration
        { delay: 5800, action: () => setAnimationStage(3) },
        // Google review prompt
        { delay: 7200, action: () => setAnimationStage(4) },
        // Typing review
        { delay: 8500, action: () => setAnimationStage(5) },
        // Review submitted
        { delay: 10500, action: () => setAnimationStage(6) },
        // Counter animation - reviews going up
        { delay: 11800, action: () => { setAnimationStage(7); setReviewCount(128); setAvgRating(4.7); }},
        // Show impact/other reviews
        { delay: 13500, action: () => setAnimationStage(8) },
        // Final stats
        { delay: 15500, action: () => setAnimationStage(9) },
      ];

      const timers = sequence.map(({ delay, action }) => setTimeout(action, delay));
      return () => timers.forEach(clearTimeout);
    }
  }, [isActive]);

  return (
    <div className="h-full flex flex-col bg-gray-950 relative overflow-hidden">
      
      {/* Celebration particles - appear on 5 stars */}
      {animationStage >= 3 && starCount === 5 && (
        <div className="absolute inset-0 pointer-events-none z-30">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 60}%`,
                backgroundColor: ['#60a5fa', '#3b82f6', '#3b82f6', '#22c55e'][i % 4],
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: '1s'
              }}
            />
          ))}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
            <Star className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-white font-bold text-xs">Review Manager</p>
            <p className="text-gray-500 text-[10px]">Automated Collection</p>
          </div>
        </div>
        {animationStage >= 7 && (
          <div className="flex items-center space-x-1 bg-green-500/20 px-2 py-1 rounded-full animate-slideUp">
            <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
            <span className="text-green-400 text-[10px] font-medium">+1 New</span>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div ref={scrollRef} className="flex-1 p-3 overflow-auto space-y-3 scroll-smooth">
        
        {/* SMS Notification */}
        {animationStage >= 1 && (
          <div className="animate-slideUp">
            <div className="bg-gray-800/80 rounded-xl rounded-tl-sm px-3 py-2 border border-white/10">
              <div className="flex items-start space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-[10px]">FS</span>
                </div>
                <div className="flex-1">
                  <p className="text-white text-xs leading-relaxed">
                    🎉 Your furniture has been delivered! How was your experience with Your Store?
                  </p>
                  <p className="text-gray-500 text-[10px] mt-1">Just now • Tap to rate</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Survey Card */}
        {animationStage >= 2 && (
          <div className="animate-slideUp">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-white/10 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 px-3 py-2 border-b border-white/10">
                <p className="text-blue-300 font-bold text-xs text-center">How was your delivery?</p>
              </div>
              <div className="p-4">
                <div className="flex justify-center space-x-2 mb-3">
                  {[1, 2, 3, 4, 5].map(i => (
                    <Star 
                      key={i}
                      className={`w-10 h-10 transition-all duration-500 transform ${
                        i <= starCount 
                          ? 'text-yellow-400 fill-current scale-110 drop-shadow-lg' 
                          : 'text-gray-600'
                      } ${i === starCount && starCount > 0 ? 'animate-bounce' : ''}`}
                      style={{
                        filter: i <= starCount ? 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.5))' : 'none',
                        transitionDelay: `${i * 50}ms`
                      }}
                    />
                  ))}
                </div>
                {starCount === 5 && (
                  <p className="text-center text-blue-300 font-bold text-sm animate-pulse">
                    ⭐ Amazing! Thank you! ⭐
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 5-Star Celebration */}
        {animationStage >= 3 && (
          <div className="animate-slideUp">
            <div className="bg-gradient-to-r from-blue-500/20 via-blue-600/20 to-blue-500/20 border border-blue-500/30 rounded-xl p-3 text-center">
              <div className="text-3xl mb-1">🎊</div>
              <p className="text-blue-300 font-bold text-sm">You're a 5-Star Customer!</p>
              <p className="text-gray-400 text-[10px] mt-1">Your feedback means everything to us</p>
            </div>
          </div>
        )}

        {/* Google Review Prompt */}
        {animationStage >= 4 && (
          <div className="animate-slideUp">
            <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-xl">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white shadow">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-xs">Share on Google</p>
                  <p className="text-gray-500 text-[10px]">Help others find great furniture!</p>
                </div>
              </div>
              
              {animationStage >= 5 && animationStage < 6 && (
                <div className="bg-gray-100 rounded-lg p-2 mb-2">
                  <p className="text-gray-800 text-xs leading-relaxed">
                    <span className="animate-pulse">|</span>
                  </p>
                  <p className="text-gray-400 text-[10px] mt-1 flex items-center">
                    <span className="w-1 h-1 bg-gray-400 rounded-full mr-1 animate-bounce" />
                    Writing review...
                  </p>
                </div>
              )}
              
              {animationStage >= 6 && (
                <div className="bg-gray-100 rounded-lg p-2 mb-2 animate-slideUp">
                  <p className="text-gray-800 text-xs leading-relaxed">
                    "Absolutely love my new sectional! Miguel was super professional and careful with everything. The whole process from purchase to delivery was seamless. Highly recommend Your Store! 🛋️"
                  </p>
                </div>
              )}
              
              <button className={`w-full py-2 rounded-lg font-bold text-xs flex items-center justify-center space-x-2 transition-all ${
                animationStage >= 6 
                  ? 'bg-green-500 text-white' 
                  : 'bg-blue-500 text-white'
              }`}>
                {animationStage >= 6 ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Review Posted!</span>
                  </>
                ) : (
                  <>
                    <Star className="w-4 h-4" />
                    <span>Post to Google</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Review Counter Animation */}
        {animationStage >= 7 && (
          <div className="animate-slideUp">
            <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-green-500/30 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-green-400 font-bold text-xs">📈 Your Google Profile</span>
                <span className="text-green-400 text-[10px] bg-green-500/20 px-2 py-0.5 rounded-full">Updated!</span>
              </div>
              <div className="flex items-center justify-around">
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-1">
                    <span className="text-white font-bold text-2xl">{avgRating}</span>
                    <Star className="w-5 h-5 text-yellow-400 fill-current" />
                  </div>
                  <p className="text-gray-400 text-[10px]">Avg Rating</p>
                </div>
                <div className="w-px h-10 bg-white/10" />
                <div className="text-center">
                  <p className="text-white font-bold text-2xl">{reviewCount}</p>
                  <p className="text-gray-400 text-[10px]">Total Reviews</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Reviews Stream */}
        {animationStage >= 8 && (
          <div className="space-y-2 animate-slideUp">
            <p className="text-gray-400 text-[10px] uppercase tracking-wider font-medium">Recent Reviews</p>
            
            {/* Maria's Review - NEW */}
            <div className="bg-gradient-to-r from-blue-500/10 to-transparent border border-blue-500/20 rounded-lg p-2.5 relative">
              <div className="absolute -top-1 -right-1 bg-blue-500 text-[8px] text-white font-bold px-1.5 py-0.5 rounded">NEW</div>
              <div className="flex items-start space-x-2">
                <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-[9px]">MG</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-1 mb-0.5">
                    <span className="text-white font-semibold text-[11px]">Maria G.</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-2.5 h-2.5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-400 text-[10px] leading-relaxed line-clamp-2">"Absolutely love my new sectional! Miguel was super professional..."</p>
                  <p className="text-gray-500 text-[9px] mt-1">Just now</p>
                </div>
              </div>
            </div>

            {/* Other recent reviews */}
            <div className="bg-white/5 border border-white/10 rounded-lg p-2.5">
              <div className="flex items-start space-x-2">
                <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-[9px]">JT</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-1 mb-0.5">
                    <span className="text-white font-semibold text-[11px]">James T.</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-2.5 h-2.5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-400 text-[10px] leading-relaxed line-clamp-2">"Best furniture shopping experience! Great prices and fast delivery."</p>
                  <p className="text-gray-500 text-[9px] mt-1">2 days ago</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Impact Stats */}
        {animationStage >= 9 && (
          <div className="animate-slideUp">
            <div className="bg-gradient-to-r from-blue-500/20 to-blue-500/20 border border-blue-500/30 rounded-xl p-3">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
                <span className="text-white font-bold text-xs">Review Impact This Month</span>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <div className="bg-white/5 rounded-lg p-2 text-center">
                  <p className="text-green-400 font-bold text-lg">+23</p>
                  <p className="text-gray-400 text-[9px]">New Reviews</p>
                </div>
                <div className="bg-white/5 rounded-lg p-2 text-center">
                  <p className="text-blue-400 font-bold text-lg">94%</p>
                  <p className="text-gray-400 text-[9px]">5-Star Rate</p>
                </div>
                <div className="bg-white/5 rounded-lg p-2 text-center">
                  <p className="text-blue-400 font-bold text-lg">+18%</p>
                  <p className="text-gray-400 text-[9px]">More Leads</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-[10px]">Estimated monthly value</span>
                  <span className="text-green-400 font-bold text-sm">+$4,200</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// THE ONLINE SALES AGENT - browser frame
// The phone demos get a phone. This one gets a browser, because the
// point is that the agent is driving a real storefront: filtering the
// grid, opening the piece, landing on the section.
//
// Composed at ONE fixed design size and scaled down to fit narrower
// columns, rather than restyled per breakpoint. A storefront has far
// more going on than a phone screen, and reflowing all of it at three
// sizes is how the type ends up unreadable at one of them.
//
// The explicit lg width matters: StorySection hands its demo column a
// `lg:w-auto` wrapper, which shrink-wraps to content. A percentage
// width inside that resolves against max-content and collapses - which
// is exactly what happened here the first time. PhoneMockup dodges it
// by setting a hard w-72/lg:w-80; this sets a hard lg:w-[580px].
// ============================================
const SA_DESIGN_W = 580;   // the desktop composition
const SA_DESIGN_W_SM = 400; // a narrower one, so phones don't scale to mush
const SA_DESIGN_H = 556;

// Two compositions, not one scaled into the ground. A single 580px design
// squeezed into a phone column lands near 0.59 scale, which drops the
// voice-bar text under 8px - unreadable, and worse than the phone demos
// beside it. Below 520px of column the frame composes at 400px instead,
// so the same type comes back up to ~0.86 scale and stays legible.
function useScaledFrame() {
  const hostRef = useRef(null);
  const [frame, setFrame] = useState({ w: SA_DESIGN_W, scale: 1 });

  useEffect(() => {
    const compute = () => {
      const el = hostRef.current;
      if (!el) return;
      // offsetWidth is the layout width: getBoundingClientRect shrinks under the
      // reveal's scale-95 and ResizeObserver never fires for a transform, so the
      // frame used to settle 5% small and off-centre on phones.
      const avail = el.offsetWidth || el.getBoundingClientRect().width;
      if (!avail) return;
      const w = avail < 520 ? SA_DESIGN_W_SM : SA_DESIGN_W;
      const scale = Math.min(1, avail / w);
      setFrame((prev) => (prev.w === w && Math.abs(prev.scale - scale) < 0.001 ? prev : { w, scale }));
    };
    compute();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(compute) : null;
    if (ro && hostRef.current) ro.observe(hostRef.current);
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
    };
  }, []);

  return { hostRef, ...frame };
}

// Real product photography, served from public/assets/storefront. Optimised
// to 560x380 (~35KB each, 412KB for the set) so the storefront reads like a
// storefront - a drawn placeholder beside the photographed phone demos is
// what made this section look foreign, and one photo repeated six times is
// what made it look lazy.
function SaShot({ img, alt, className = '' }) {
  return (
    <div className={`relative overflow-hidden bg-slate-900 ${className}`}>
      <img
        src={`/assets/storefront/${img}.jpg`}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
    </div>
  );
}

function BrowserMockup({ children, caption, url = 'bluebirdhome.com' }) {
  const { hostRef, w: designW, scale } = useScaledFrame();

  return (
    <div ref={hostRef} className="w-full lg:w-[580px] flex flex-col items-center">
      {/* Caption is mobile-only, the same as PhoneMockup's - on desktop
          the other story sections show no pill, and this one shouldn't either. */}
      {caption && (
        <div className="lg:hidden mb-2.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-200">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400" aria-hidden="true" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600">{caption}</span>
        </div>
      )}

      <div className="relative max-w-none" style={{ width: designW * scale, maxWidth: 'none', height: SA_DESIGN_H * scale }}>
        <div className="absolute -inset-5 lg:-inset-8 bg-gradient-to-r from-blue-500/10 to-blue-500/10 rounded-[2.5rem] blur-2xl" aria-hidden="true" />
        <div
          className="absolute top-0 left-0 max-w-none"
          style={{ width: designW, maxWidth: 'none', height: SA_DESIGN_H, transform: `scale(${scale})`, transformOrigin: 'top left' }}
        >
          <div className="relative w-full h-full rounded-2xl bg-gradient-to-b from-gray-800 to-gray-900 p-2.5 shadow-2xl border border-gray-700">
            {/* Tab strip */}
            <div className="flex items-end gap-2 px-1 mb-2">
              <div className="flex items-center gap-2 pb-2">
                <span className="w-3 h-3 rounded-full bg-red-400/80" />
                <span className="w-3 h-3 rounded-full bg-yellow-400/80" />
                <span className="w-3 h-3 rounded-full bg-green-400/80" />
              </div>
              <div className="flex items-center gap-2 px-3.5 py-2 rounded-t-lg bg-gray-950 border-t border-x border-gray-700 min-w-0">
                <span className="w-3.5 h-3.5 rounded-sm bg-blue-500/80 flex-shrink-0" />
                <span className="text-[11px] text-slate-300 font-medium truncate">Bluebird Home · Sectionals</span>
                <X className="w-3 h-3 text-slate-400 flex-shrink-0" />
              </div>
            </div>
            {/* URL bar */}
            <div className="flex items-center gap-2.5 px-1 mb-2">
              <div className="flex items-center gap-1.5 text-slate-400 flex-shrink-0">
                <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                <ArrowRight className="w-3.5 h-3.5 opacity-40" />
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-950/90 border border-gray-700">
                <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-green-400/80 flex-shrink-0" aria-hidden="true">
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
                <span className="text-[11px] text-slate-400 font-medium truncate">{url}</span>
              </div>
            </div>
            {/* Viewport */}
            <div className="relative w-full h-[452px] bg-gray-950 rounded-lg overflow-hidden border border-gray-700">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// The floor, as the grid shows it before anyone asks for anything.
const SA_FLOOR = [
  { name: 'Kensington Sofa', price: '$1,290', img: 'kensington-camel', note: 'Camel' },
  { name: 'Alameda Sectional', price: '$1,690', img: 'alameda-forest', note: 'Forest' },
  { name: 'Rockridge Frameless', price: '$1,150', img: 'rockridge-fog', note: 'Fog' },
  { name: 'Elmwood Sofa', price: '$1,940', img: 'elmwood-mahogany', note: 'Mahogany' },
  { name: 'Montclair Sleeper', price: '$1,120', img: 'montclair-indigo', note: 'Indigo' },
  { name: 'Danville Sectional', price: '$1,480', img: 'danville-black', note: 'Black' },
];

// What survives "grey, under $1,500".
const SA_MATCHES = [
  { name: 'Danville Sectional', price: '$1,480', img: 'danville-slate', note: 'Slate · 4 seats' },
  { name: 'Rockridge Frameless', price: '$1,150', img: 'rockridge-fog', note: 'Fog · 4 seats' },
  { name: 'Claremont Sofa', price: '$1,320', img: 'claremont-charcoal', note: 'Charcoal · 3 seats' },
];

// The upsell: what the agent adds to the sale, and why it picked each.
const SA_PAIRS = [
  { name: 'Albany Chair', price: '$480', why: 'Same grey family, softer line.', img: 'albany-chair' },
  { name: 'Claremont Ottoman', price: '$295', why: 'Pulls the seating together.', img: 'claremont-ottoman' },
  { name: 'Albany Loveseat', price: '$690', why: 'Finishes the second wall.', img: 'albany-loveseat' },
];

// THE VARIABLES CARD - the one still picture of the product. A rep on one
// of our floors could not find a piece for a customer's wall, colour and
// budget; the kiosk only searches by name. The customer's variables went
// into the platform and real options came back (Phillip, 2026-10-30).
// No motion: the page sells outcomes, not demos.
const VC_VARIABLES = ['Sectional', 'Fits a 96 in wall', 'Grey', 'Under $1,500', 'In stock'];
function VariablesCard({ wide = false }) {
  return (
    <div className={`h-full w-full bg-slate-950 text-white flex flex-col ${wide ? 'p-6' : 'p-4 pt-5'}`}>
      <p className="text-[9px] font-semibold uppercase tracking-[0.22em] text-blue-300">Sales floor · what the customer needs</p>
      <div className={`mt-3 flex flex-wrap gap-1.5 ${wide ? 'max-w-xs' : ''}`}>
        {VC_VARIABLES.map((v) => (
          <span key={v} className="px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-semibold">{v}</span>
        ))}
      </div>
      <p className="mt-4 text-[11px] text-slate-400">3 real options from every brand you carry</p>
      <div className={`mt-2 flex-1 min-h-0 ${wide ? 'grid grid-cols-3 gap-3' : 'flex flex-col gap-2'}`}>
        {SA_MATCHES.map((m) => (
          <div key={m.name} className={`rounded-xl bg-white/5 border border-white/10 overflow-hidden ${wide ? 'flex flex-col' : 'flex items-center gap-3 p-2'}`}>
            <SaShot img={m.img} alt={m.name} className={wide ? 'w-full h-44' : 'w-14 h-14 rounded-lg flex-shrink-0'} />
            <div className={`${wide ? 'p-3' : ''} min-w-0`}>
              <p className="text-[12px] font-semibold leading-tight truncate">{m.name}</p>
              <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{m.note}</p>
              <p className="text-[12px] font-bold text-blue-300 mt-1">{m.price}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-full bg-blue-600 text-center py-2 text-[11px] font-bold">Text the list</div>
        <div className="rounded-full bg-white/10 border border-white/15 text-center py-2 text-[11px] font-semibold">Run credit</div>
      </div>
    </div>
  );
}

// Every line the two of them say. One turn is typed rather than spoken,
// because "or type" is half the claim and a caption alone won't sell it.
const SA_TURNS = {
  1: { who: 'you', text: 'Grey sectional, under $1,500.' },
  2: { who: 'ai', text: 'Three on the floor. All in stock.' },
  3: { who: 'you', text: 'open the second one', typed: true },
  4: { who: 'ai', text: 'The Rockridge, in Fog. $1,150.' },
  5: { who: 'you', text: 'What would finish the room?' },
  6: { who: 'ai', text: 'These three. The oak warms the grey.' },
  7: { who: 'you', text: 'Add them all.' },
  8: { who: 'ai', text: 'Four pieces. $2,615.' },
};

const SA_ACTIONS = {
  0: 'Reading the full catalog · 1,240 pieces',
  2: 'Filtered · sectionals, grey, under $1,500',
  4: 'Opened Rockridge Frameless · colourway Fog',
  6: 'Matched 3 pieces to the Fog',
  8: 'Added 4 pieces · cart $2,615',
};
const SA_ACTION_KEYS = [8, 6, 4, 2, 0];

function SalesAgentContent({ isActive }) {
  const [beat, setBeat] = useState(0);
  const startedRef = useRef(false);
  const timersRef = useRef([]);


  useEffect(() => {
    if (!isActive || startedRef.current) return;
    startedRef.current = true;

    const TIMELINE = [
      // ---- it knows the whole catalog ----
      { t: 0, b: 0 },       // every SKU on the floor
      { t: 1300, b: 1 },    // spoken: type, colour and budget at once
      { t: 3300, b: 2 },    // the variables land as filters
      // ---- it drives the site, spoken or typed ----
      { t: 5600, b: 3 },    // typed this time, not spoken
      { t: 7000, b: 4 },    // the piece opens, in the right colourway
      // ---- it builds the rest of the sale ----
      { t: 9300, b: 5 },    // "what would finish the room?"
      { t: 10700, b: 6 },   // three suggestions, each with its reason
      { t: 13500, b: 7 },   // "add them all"
      { t: 14900, b: 8 },   // one sofa becomes a room
    ];

    const clearAll = () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };
    const run = () => {
      clearAll();
      TIMELINE.forEach(({ t, b }) => timersRef.current.push(setTimeout(() => setBeat(b), t)));
    };
    run();

    return undefined;
  }, [isActive]);

  useEffect(() => () => { timersRef.current.forEach(clearTimeout); }, []);

  const onProduct = beat >= 4;
  const filtered = beat >= 2;
  const action = SA_ACTIONS[SA_ACTION_KEYS.find((k) => beat >= k)];
  const turn = SA_TURNS[beat];
  const speaking = turn?.who === 'you' && !turn.typed;
  const typing = turn?.who === 'you' && turn.typed;

  return (
    <div className="absolute inset-0 flex flex-col bg-gray-950">
      {/* Store header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 h-11 border-b border-white/[0.07] bg-gray-900/70">
        <div className="flex items-center gap-5">
          <span className="text-[13px] font-bold tracking-[0.18em] text-white uppercase">Bluebird Home</span>
          <span className="hidden sm:flex items-center gap-4 text-[11px] font-medium text-gray-500">
            <span className={onProduct ? '' : 'text-blue-300'}>Sectionals</span>
            <span>Sofas</span>
            <span>Dining</span>
            <span>Bedroom</span>
          </span>
        </div>
        <div className="flex items-center gap-3.5 text-gray-500">
          <Heart className="w-4 h-4" />
          <div className="relative">
            <ShoppingCart className="w-4 h-4" />
            {beat >= 8 && (
              <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-blue-500 text-[8px] font-bold text-white flex items-center justify-center animate-fadeIn">4</span>
            )}
          </div>
        </div>
      </div>

      {/* What the agent just did to the page */}
      <div className="flex-shrink-0 flex items-center gap-2 px-4 h-[26px] bg-blue-500/10 border-b border-blue-400/20">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse flex-shrink-0" aria-hidden="true" />
        <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-blue-300 flex-shrink-0">Agent</span>
        <span key={action} className="text-[10px] text-gray-300 font-medium truncate animate-fadeIn">{action}</span>
      </div>

      {/* Page */}
      <div className="flex-1 min-h-0 overflow-hidden relative">
        {!onProduct ? (
          /* ---------- CATALOG ---------- */
          <div className="p-4">
            <div className="flex items-baseline justify-between mb-2.5">
              <span className="text-[15px] font-bold text-white tracking-tight">Sectionals</span>
              <span className={`text-[11px] font-semibold transition-colors duration-500 ${filtered ? 'text-blue-300' : 'text-gray-500'}`}>
                {filtered ? '3 of 1,240 pieces' : '1,240 pieces'}
              </span>
            </div>

            {/* The variables, landed as filters. The agent set these. */}
            <div className="flex flex-wrap gap-2 mb-3.5 min-h-[24px]">
              {filtered && ['Sectionals', 'Grey', 'Under $1,500'].map((c, i) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/20 border border-blue-400/40 text-[10px] font-semibold text-blue-200 animate-fadeIn"
                  style={{ animationDelay: `${i * 110}ms`, animationFillMode: 'backwards' }}
                >
                  {c}
                  <X className="w-2.5 h-2.5 opacity-60" />
                </span>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3">
              {(filtered ? SA_MATCHES : SA_FLOOR).map((p, i) => (
                <div
                  key={p.name}
                  className={`rounded-lg border overflow-hidden animate-fadeIn transition-all duration-500 ${
                    filtered && i === 1 && beat >= 3
                      ? 'border-blue-400/70 ring-2 ring-blue-400/30'
                      : 'border-white/10'
                  }`}
                  style={{ animationDelay: `${i * 70}ms`, animationFillMode: 'backwards' }}
                >
                  <SaShot img={p.img} alt={p.name} className="h-[92px]" />
                  <div className="px-2.5 py-2 bg-white/[0.03] border-t border-white/[0.06]">
                    <p className="text-[10px] text-gray-300 font-semibold leading-tight truncate">{p.name}</p>
                    <p className="text-[12px] text-white font-bold mt-0.5">{p.price}</p>
                    {p.note && <p className="text-[9px] text-blue-300/90 font-medium truncate mt-0.5">{p.note}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* ---------- PRODUCT ---------- */
          <div className="p-4">
            <div className="flex gap-3.5 mb-4">
              <div className="w-[34%] flex-shrink-0 relative rounded-lg h-[122px] overflow-hidden border border-white/10">
                <SaShot img="rockridge-fog" alt="Rockridge Frameless Sectional in Fog" className="w-full h-full" />
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-yellow-400 text-[9px] font-extrabold text-gray-900 uppercase tracking-wide shadow">
                  On the floor
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[16px] font-bold text-white leading-tight tracking-tight">Rockridge Frameless Sectional</p>
                <p className="text-[20px] font-bold text-white mt-1">$1,150</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  <span className="px-2 py-0.5 rounded-md bg-white/10 border border-white/15 text-[9.5px] font-bold text-gray-200">4 seats</span>
                  <span className="px-2 py-0.5 rounded-md bg-white/10 border border-white/15 text-[9.5px] font-bold text-gray-200">Performance weave</span>
                  <span className="px-2 py-0.5 rounded-md bg-green-500/15 border border-green-400/30 text-[9.5px] font-bold text-green-300">In stock</span>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  {[
                    { c: '#8b8f95', n: 'Fog' },
                    { c: '#5f6b74', n: 'Slate' },
                    { c: '#b9a893', n: 'Oat' },
                  ].map((s, i) => (
                    <span
                      key={s.n}
                      className={`w-5 h-5 rounded-full border transition-all duration-500 ${
                        i === 0 ? 'border-blue-400 ring-2 ring-blue-400/40 scale-110' : 'border-white/20'
                      }`}
                      style={{ background: s.c }}
                      title={s.n}
                    />
                  ))}
                  <span className="text-[11px] text-blue-300 font-semibold ml-1">Fog</span>
                </div>
              </div>
            </div>

            {/* ---- THE UPSELL ---- */}
            <div
              className={`rounded-xl border p-3.5 transition-all duration-700 ${
                beat >= 6 ? 'border-blue-400/60 bg-blue-500/[0.08] shadow-lg shadow-blue-500/10' : 'border-white/10 bg-white/[0.02]'
              }`}
            >
              <div className="flex items-center gap-2.5 mb-2.5">
                <span className="w-7 h-7 rounded-lg bg-blue-500/20 border border-blue-400/30 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-blue-300" />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-blue-300">Completes the room</span>
              </div>
              {beat >= 6 ? (
                <div className="grid grid-cols-3 gap-3 animate-fadeIn">
                  {SA_PAIRS.map((p, i) => (
                    <div
                      key={p.name}
                      className="rounded-lg border border-white/10 overflow-hidden animate-fadeIn"
                      style={{ animationDelay: `${i * 130}ms`, animationFillMode: 'backwards' }}
                    >
                      <SaShot img={p.img} alt={p.name} className="h-[52px]" />
                      <div className="px-2 py-1.5">
                        <div className="flex items-baseline justify-between gap-1">
                          <p className="text-[10px] font-bold text-gray-200 leading-tight truncate">{p.name}</p>
                          <p className="text-[10px] font-bold text-white flex-shrink-0">{p.price}</p>
                        </div>
                        <p className="text-[9px] text-blue-300/90 leading-snug mt-0.5">{p.why}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-gray-500">Ask what goes with it.</p>
              )}
            </div>
          </div>
        )}

        {/* ---- CART - one sofa became a room ---- */}
        {beat >= 8 && (
          <div className="absolute inset-y-0 right-0 w-[42%] bg-gray-900 border-l border-white/10 shadow-2xl p-3.5 animate-slideInRight z-20 flex flex-col">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-white">Your cart · 4</span>
              <X className="w-4 h-4 text-gray-500" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <SaShot img="rockridge-fog" alt="Rockridge Frameless Sectional" className="w-9 h-9 rounded-md flex-shrink-0 border border-white/10" />
                <div className="min-w-0 flex-1">
                  <p className="text-[9.5px] font-bold text-gray-200 leading-tight truncate">Rockridge Frameless</p>
                  <p className="text-[9px] text-gray-500">Fog</p>
                </div>
                <span className="text-[10px] font-bold text-white flex-shrink-0">$1,150</span>
              </div>
              {SA_PAIRS.map((p, i) => (
                <div
                  key={p.name}
                  className="flex items-center gap-2 animate-fadeIn"
                  style={{ animationDelay: `${120 + i * 110}ms`, animationFillMode: 'backwards' }}
                >
                  <SaShot img={p.img} alt={p.name} className="w-9 h-9 rounded-md flex-shrink-0 border border-white/10" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[9.5px] font-bold text-gray-200 leading-tight truncate">{p.name}</p>
                    <p className="text-[9px] text-blue-300/80">Suggested</p>
                  </div>
                  <span className="text-[10px] font-bold text-white flex-shrink-0">{p.price}</span>
                </div>
              ))}
            </div>

            <div className="mt-auto pt-3 border-t border-white/10">
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] text-gray-400 font-semibold">Subtotal</span>
                <span className="text-[16px] text-white font-bold">$2,615</span>
              </div>
              <p className="text-[9px] text-blue-300 font-semibold mt-0.5">+$1,465 the agent suggested</p>
              <div className="mt-2.5 w-full py-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-center text-[11px] font-bold text-white">
                Checkout
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ---- VOICE OR TEXT - the same agent either way ---- */}
      <div className="flex-shrink-0 border-t border-white/10 bg-gray-900/95 backdrop-blur px-4 h-16 flex items-center gap-3.5">
        <div className="relative flex-shrink-0">
          {speaking && <span className="absolute -inset-2 rounded-full bg-blue-500/30 animate-ping" aria-hidden="true" />}
          <div className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
            speaking ? 'bg-gradient-to-br from-blue-400 to-blue-600 scale-105 shadow-lg shadow-blue-500/30' : 'bg-white/10 border border-white/15'
          }`}>
            <Mic className={`w-5 h-5 ${speaking ? 'text-white' : 'text-gray-400'}`} />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          {turn ? (
            <div key={beat} className="animate-fadeIn">
              <p className={`text-[9px] font-bold uppercase tracking-[0.18em] mb-0.5 flex items-center gap-1.5 ${turn.who === 'you' ? 'text-blue-300' : 'text-gray-500'}`}>
                {turn.who === 'you' ? 'Shopper' : 'Exitium'}
                {turn.typed && (
                  <span className="px-1.5 py-[1px] rounded bg-white/10 border border-white/15 text-[7.5px] tracking-normal text-gray-300">typed</span>
                )}
              </p>
              <p className={`text-[13px] leading-snug ${turn.who === 'you' ? 'text-white font-semibold' : 'text-gray-300'}`}>
                {turn.text}
                {typing && <span className="inline-block w-[2px] h-[13px] translate-y-[2px] ml-[3px] bg-blue-400 animate-pulse" aria-hidden="true" />}
              </p>
            </div>
          ) : (
            <p className="text-[12px] text-gray-500 font-medium">Talk to it, or type. Same agent either way.</p>
          )}
        </div>

        <div className="flex items-end gap-[3px] h-7 flex-shrink-0" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <span
              key={i}
              className={`w-[3px] rounded-full transition-all duration-300 ${speaking ? 'bg-blue-400 animate-pulse' : 'bg-gray-700'}`}
              style={{
                height: speaking ? `${[9, 18, 26, 14, 20, 8][i]}px` : '5px',
                animationDelay: `${i * 90}ms`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// STORY SECTION COMPONENT
// ============================================
// One cell of the leak band: number counts up when the band scrolls
// into view, staggered left to right, with its source underneath.
// ============================================
// Still numbers (2026-10-30: no count-ups, no fades; the page sells outcomes).
function LeakStat({ end, prefix = '', suffix = '', label, src }) {
  return (
    <div className="text-center md:text-left">
      <p className="text-slate-900 text-3xl md:text-4xl font-bold tracking-tight mb-1 tabular-nums">
        {prefix}
        {end.toLocaleString('en-US')}
        {suffix}
      </p>
      <p className="text-slate-600 text-xs md:text-sm leading-snug">{label}</p>
      {src && (
        <p className="text-slate-400 text-[10px] md:text-[11px] mt-1.5">{src}</p>
      )}
    </div>
  );
}

// CLIENT LOGO STRIP - the stores we build for, sliding under the hero.
// Every mark is a one-tone white silhouette (public/assets/clients) so the
// four brands read as one neutral row; the row is rendered twice and slid
// by half its width so the loop never shows a seam. Stores with a site link
// out; 209 Furniture has none, so its mark is plain. Hover pauses the slide
// and lights the mark up; reduced-motion gets a static, wrapping row.
const CLIENT_LOGOS = [
  { name: 'Carmichael Furniture Outlet', img: '/assets/clients/carmichael.png', href: 'https://www.carmichaelfurnitureoutlet.com/', h: 'h-9 md:h-14' },
  { name: 'Davis Furniture Outlet', img: '/assets/clients/davis.png', href: 'https://yourdavisfurniture.com/', h: 'h-10 md:h-16' },
  { name: 'East Bay Furniture', img: '/assets/clients/eastbay.png', href: 'https://eastbay.direct/', h: 'h-10 md:h-16' },
  { name: '209 Furniture', img: '/assets/clients/209.png', href: null, h: 'h-6 md:h-9' },
];

function ClientLogoStrip() {
  const Mark = ({ logo }) => {
    const img = (
      <img
        src={logo.img}
        alt={logo.name}
        className={`${logo.h} w-auto object-contain brightness-0`}
        loading="lazy"
        decoding="async"
        draggable="false"
      />
    );
    const cls = 'client-logo flex items-center shrink-0 opacity-40 transition-opacity duration-300 hover:opacity-80 focus-visible:opacity-80 outline-none';
    return logo.href ? (
      <a href={logo.href} target="_blank" rel="noopener noreferrer" className={cls} title={logo.name} aria-label={`${logo.name} (opens in a new tab)`}>
        {img}
      </a>
    ) : (
      <span className={cls} title={logo.name}>{img}</span>
    );
  };

  // Two copies of the row: the track slides exactly one copy's width, then
  // snaps back to 0, which is pixel-identical, so the loop is seamless.
  // Each row is at least a screen plus 16rem wide (see the style block) with
  // the marks spread evenly, so a logo and its copy are never on screen at
  // the same time, not even one leaving while the other enters.
  const Row = ({ hidden }) => (
    <div className="client-row flex items-center gap-12 md:gap-24" aria-hidden={hidden || undefined}>
      {CLIENT_LOGOS.map((logo) => <Mark key={logo.name} logo={logo} />)}
    </div>
  );

  return (
    <section className="px-4 pt-6 md:pt-2 pb-2 md:pb-6" aria-label="Stores we work with">
      <style>{`
        @keyframes clientSlide { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        /* index.css caps every div at max-width:100% under 1024px; the track must be wider than the page */
        .client-track, .client-row { max-width: none; flex-shrink: 0; }
        .client-row { min-width: calc(100vw + 16rem); justify-content: space-around; }
        .client-track { animation: clientSlide 32s linear infinite; will-change: transform; }
        .client-marquee:hover .client-track { animation-play-state: paused; }
        .client-marquee { -webkit-mask-image: linear-gradient(to right, transparent, #000 24%, #000 76%, transparent); mask-image: linear-gradient(to right, transparent, #000 24%, #000 76%, transparent); }
        @media (min-width: 768px) { .client-marquee { -webkit-mask-image: linear-gradient(to right, transparent, #000 10%, #000 90%, transparent); mask-image: linear-gradient(to right, transparent, #000 10%, #000 90%, transparent); } }
        @media (prefers-reduced-motion: reduce) {
          .client-track { animation: none; flex-wrap: wrap; justify-content: center; }
          .client-track .client-row[aria-hidden] { display: none; }
          .client-track .client-row { flex-wrap: wrap; justify-content: center; min-width: 0; row-gap: 1.25rem; }
          .client-marquee { -webkit-mask-image: none; mask-image: none; }
        }
      `}</style>
      <div className="max-w-6xl mx-auto">
        <p className="text-center text-slate-400 text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.28em] mb-4 md:mb-5">
          Stores we work with
        </p>
        <div className="client-marquee overflow-hidden">
          <div className="client-track flex w-max">
            <Row />
            <Row hidden />
          </div>
        </div>
      </div>
    </section>
  );
}

// Visible FAQ accordion. First question open by default so the
// section never reads as a wall of closed rows.
function FAQSection() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-16 md:py-24 px-4 border-t border-slate-200 bg-slate-50">
      <div className="container mx-auto max-w-3xl">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-blue-600 font-semibold uppercase tracking-[0.28em] text-[10px] md:text-xs mb-4 md:mb-5">FAQ</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
            Frequently asked{' '}
            <span className="text-blue-600">questions.</span>
          </h2>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_40px_-16px_rgba(15,23,42,0.14)] divide-y divide-slate-200">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i}>
              <button
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
                className="w-full flex items-center justify-between gap-4 text-left px-5 md:px-6 py-4 md:py-5"
              >
                <span className="text-slate-900 font-semibold text-sm md:text-base leading-snug">{item.q}</span>
                <ChevronDown
                  className={`w-4 h-4 md:w-5 md:h-5 text-blue-600 flex-shrink-0 transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`}
                />
              </button>
              <div
                className={`grid transition-all duration-300 ease-out ${open === i ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
              >
                <div className="overflow-hidden">
                  <p className="px-5 md:px-6 pb-4 md:pb-5 text-slate-600 text-sm md:text-base leading-relaxed">{item.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// A story is words first (2026-10-30: no scroll dimming, no slide-ins, no
// auto-playing demos). With children it is two columns on lg; without, one
// centred column of copy.
function StorySection({ step, kicker, title, description, children, align = 'left', proof, sectionRef, stat, id, ctaHref = 'https://calendly.com/byars-jp5/30min', ctaLabel = 'See it live' }) {
  const solo = !children;
  return (
    <div id={id} ref={sectionRef} className={`${solo ? 'py-14 md:py-24' : 'py-12 md:py-20 lg:py-28'} px-4 scroll-mt-16 border-t border-slate-200`}>
      <div className={`container mx-auto ${solo ? 'max-w-3xl' : 'max-w-7xl'}`}>
        <div className={solo ? 'flex flex-col' : 'flex flex-col lg:grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center'}>

          {/* Mobile: headline leads, so the picture below it has context */}
          {!solo && (
            <div className="lg:hidden order-1 text-center">
              {kicker && (
                <p className="text-blue-600 font-semibold uppercase tracking-[0.28em] text-[10px] mb-2.5">
                  {String(step).padStart(2, '0')} · {kicker}
                </p>
              )}
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-[1.1] tracking-tight">
                {title}
              </h2>
            </div>
          )}

          {/* The still picture, after the headline on mobile, beside the copy on lg */}
          {!solo && (
            <div className={`${align === 'right' ? 'lg:order-1' : 'lg:order-2'} order-2 w-full min-w-0 flex justify-center`}>
              <div className="w-full lg:w-auto min-w-0 flex justify-center">
                {children}
              </div>
            </div>
          )}

          {/* Text */}
          <div className={solo ? '' : `${align === 'right' ? 'lg:order-2' : 'lg:order-1'} order-3`}>
            <div className={solo ? 'text-center' : 'text-center lg:text-left'}>
              <div className={solo ? '' : 'hidden lg:block'}>
                {kicker && (
                  <p className="text-blue-600 font-semibold uppercase tracking-[0.28em] text-[10px] md:text-xs mb-3 md:mb-5">
                    {String(step).padStart(2, '0')} · {kicker}
                  </p>
                )}
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 md:mb-6 leading-[1.1] tracking-tight">
                  {title}
                </h2>
              </div>
              {stat && (
                <div className={`mb-4 md:mb-5 flex items-baseline justify-center gap-2.5 md:gap-3 ${solo ? '' : 'lg:justify-start'}`}>
                  <span className="shrink-0 text-2xl md:text-3xl lg:text-4xl font-bold text-blue-600 tracking-tight whitespace-nowrap">
                    {stat.value}
                  </span>
                  <span className="text-left text-slate-600 text-xs md:text-sm leading-snug max-w-xs">
                    {stat.line}
                    {stat.src && (
                      <span className="block text-slate-400 text-[10px] md:text-[11px] mt-0.5">{stat.src}</span>
                    )}
                  </span>
                </div>
              )}
              <p className="text-sm md:text-lg lg:text-xl text-slate-600 leading-snug md:leading-relaxed mb-4 md:mb-6">
                {description}
              </p>
              {proof && (
                <p className="text-xs md:text-sm text-slate-500 border-l-2 border-blue-500 pl-3 md:pl-4 mb-5 md:mb-8 max-w-lg mx-auto lg:mx-0 text-left">
                  {proof}
                </p>
              )}
              <div className={`flex flex-row flex-wrap sm:flex-nowrap gap-2.5 md:gap-4 justify-center ${solo ? '' : 'lg:justify-start'}`}>
                <a
                  href={ctaHref}
                  target={ctaHref.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center space-x-2 px-5 md:px-7 py-2.5 md:py-3 bg-white border border-blue-600 text-blue-700 rounded-full font-semibold text-sm md:text-base hover:bg-blue-50 transition-all duration-300"
                >
                  <span>{ctaLabel}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// LIVE DEMO LINE - "call the demo store"
// Las Vegas Market booth — single source of truth for the hero button.
const VEGAS_DATES = 'July 26-30';
const VEGAS_BOOTH = 'Booth 10 \u00b7 HFA Resource Center \u00b7 Bldg B, Floor 10';
const VEGAS_BOOTH_SHORT = 'Booth 10 \u00b7 Bldg B, Fl 10'; // fits on one line inside the CTA
const DEMO_STORE_NAME = 'Bluebird Home Furnishings';
const DEMO_LINE_NUMBER = '(XXX) XXX-XXXX';
const DEMO_LINE_TEL = 'tel:+1XXXXXXXXXX';

// ============================================
// HERO COPY
// Two live directions. Change HERO_DEFAULT to ship one,
// or append ?hero=B to the URL to compare them in the
// browser without a rebuild (a small switcher appears
// whenever the param is present, and only then).
//
// Structure of each variant:
//   eyebrow  - category framing, demoted under the headline
//   headline - array of rendered lines, staggered on load
//   subhead  - mechanism (we answer, then the receipt lives on)
//   kicker   - the differentiator, set in white as the one emphasis
// ============================================
const HERO_COPY = {
  A: {
    id: 'A',
    label: 'Three places at once',
    eyebrow: 'The AI Layer for Furniture Retail',
    headline: [
      { text: 'One AI that sells', gradient: false },
      { text: 'on your floor, your website,', gradient: true },
      { text: 'and your phone.', gradient: true },
    ],
    subhead: 'It sells on your ecommerce site, helps customers in your showroom, answers your phone, texts a digital receipt, and asks for the review.',
    kicker: 'Customers get answers. You get the sale.',
  },
  B: {
    id: 'B',
    label: 'Nobody works the website',
    eyebrow: 'The AI Layer for Furniture Retail',
    headline: [
      { text: 'You staffed the floor.', gradient: false },
      { text: 'You staffed the phone.', gradient: false },
      { text: 'Nobody works the site.', gradient: true },
    ],
    subhead: 'Exitium covers all three with one AI: it answers every call with your whole catalog in memory, sells to shoppers on your website by voice, and texts a receipt the customer keeps replying to for months.',
    kicker: 'Every surface your customer touches. One agent behind all of them.',
  },
  C: {
    id: 'C',
    label: 'The next store answers',
    eyebrow: 'The AI Layer for Furniture Retail',
    headline: [
      { text: 'Every call you miss,', gradient: false },
      { text: 'the next store', gradient: true },
      { text: 'answers.', gradient: true },
    ],
    subhead: 'Exitium picks up on the first ring knowing every piece on your floor and every order in your book, then texts the buyer an itemized receipt they keep replying to for months.',
    kicker: 'Most software stops at the sale. Ours starts there.',
  },
  // D is the product, not the features (Phillip, 2026-10-29: "I've been
  // building a lot of features, not a product... it's your website, it's
  // your designer, it's your sales floor"). Two tiers on one spine: the
  // website, the catalog with the designer, the store with checkout and
  // the floor. Everything is in both tiers; the tier is the size of the store.
  D: {
    id: 'D',
    label: 'The sales platform',
    eyebrow: 'The sales platform for furniture stores',
    headline: [
      { text: 'Your website.', gradient: false },
      { text: 'Your designer.', gradient: true },
      { text: 'Your sales floor.', gradient: true },
    ],
    subhead: 'One platform the store runs on. A website Google and AI can read, a designer that finds what fits the customer’s room, and a sales floor that seats them, closes, and texts the receipt.',
    kicker: 'Start with the store. Grow into the platform.',
  },
  // E is the outcome (Phillip, 2026-10-29: "less selling features and more
  // selling the outcomes"). A store sells what is on the floor; with the
  // platform it sells every piece it can order, to shoppers who found it
  // online, on the floor, and on the phone.
  E: {
    id: 'E',
    label: 'Sell more of what you carry',
    eyebrow: 'The sales platform for furniture stores',
    headline: [
      { text: 'Sell every piece', gradient: false },
      { text: 'you can order,', gradient: true },
      { text: 'not just the floor.', gradient: true },
    ],
    subhead: 'Shoppers find your store on Google and in AI and arrive with a cart. Your rep closes on the floor and the customer leaves with the list on their phone. Every call is answered.',
    kicker: 'Running every day at eastbay.direct.',
  },
  // F is the product as a Wondersign replacement (Phillip, 2026-10-30: "a
  // Wondersign in the hand of the customer and the sales floor, and they
  // can walk out with it or it can be completely handled online"). The
  // outcome, not the features.
  F: {
    id: 'F',
    label: 'In the customer’s hand',
    eyebrow: 'The sales platform for furniture stores',
    headline: [
      { text: 'Every piece you carry,', gradient: false },
      { text: 'in your customer’s hand.', gradient: true },
    ],
    subhead: 'Your whole catalog, every brand you carry, on your customer’s phone and on your sales floor. The rep enters what the customer needs, the size, the colour, the budget, and real pieces come back with prices. The customer keeps the list and buys in the store or at home online. Same list, same price.',
    kicker: '',
  },
  // G is who we are, said outright (Phillip, 2026-10-31: "direct to who we
  // actually are... the sales platform for furniture, in store and
  // ecommerce, the in-store designer and salesman platform... the
  // replacement of the catalog kiosk; everything else is a feature"). The
  // category in the headline, the mechanism in the subhead, the one
  // difference from a kiosk as the kicker.
  G: {
    id: 'G',
    label: 'Who we are',
    eyebrow: '',
    headline: [
      { text: 'The virtual designer', gradient: false },
      { text: 'for furniture stores.', gradient: true },
    ],
    // Phillip, 2026-11-02: "a Wondersign replacement, Wondersign on steroids";
    // say virtual designer, not AI. Researched 2026-11-02: Wondersign's kiosk
    // is category tiles, a search bar, a filter panel and a sort menu (A to Z,
    // brand, price, best sellers); no natural language, no dimensions, no AI
    // on their site; the only take-home is a cart shared by text.
    // Phillip, 2026-11-02 (later): "too much copy, not clear what the
    // product is; I need to explain it in one sentence". So the hero is the
    // one sentence and nothing else; no kicker, phones show the same line.
    // And shorter still (Phillip, same evening): people do not read; it is
    // a website with ecommerce and the rest of the features, say that.
    subhead: 'Finds the exact piece and sells it. Your website, checkout, showroom and phone, all in one.',
    kicker: '',
  },
};
const HERO_DEFAULT = 'G';

// ============================================
// RESEARCH - the numbers behind the pitch
// Same canon as the booth loop's "leak" scene, so the site a
// prospect opens on their phone agrees with the screen at B-1050.
// LEAK_STATS renders as the animated band right under the hero;
// STORY_STATS attach one researched number to each feature story.
// ============================================
// PRICING - two tiers, per showroom (Phillip, 2026-10-29: "499 is just too
// cheap for what eastbay.direct is"; restaurants stay on the $197 sites for
// volume, this side goes for quality). Store is the catalog with checkout,
// at what the furniture website vendors charge for a plain site. Platform is
// the product: the designer, the sales floor, the customer record, and us
// reading the store's numbers and tuning the copy every month. The phone
// agent, the receipt, deliveries and reviews are in both tiers.
const SETUP = 'Plus $2,500 setup: the catalog built and the photos placed. Waived on an annual contract.';
// Both tiers hold the whole platform. The tier is the size of the store, not
// the list of features (Phillip, 2026-10-29: a store like East Bay will pay
// $497 for all of it and grow; a bigger store sees why it pays $997 when the
// difference is reps, minutes and a direct line, things it can count on its
// own floor). Deliveries come with both.
const EVERYTHING = ['Every piece you carry is for sale online, not just what fits on the floor', 'Shoppers who ask Google or AI for furniture near them land on a page that sells', 'The designer takes the room, the size, the colour and the budget, by voice or text, in English or Spanish, and finds what fits', 'Your reps close on the floor and the customer leaves with the list on their phone, so the sale is not lost to the drive home', 'The phone agent answers every call knowing your floor and every open order, and transfers to staff on shift', 'Every sale sends a receipt by text that answers the customer’s questions, any hour, so your phone rings less', 'Deliveries planned from the receipts: the route for the truck, the driver’s stops on their phone, and the customer’s copy updated at the door', 'Every delivered order asks for the Google review', 'One record per customer from the first visit to the truck, whoever answered', 'They pay on your own card processor, card fees at cost, with financing on every price'];
const PRICING_TIERS = [
  {
    name: 'Store', price: '$497', setup: SETUP,
    line: 'The whole platform, sized for one showroom: up to three reps on the floor and about 300 phone minutes a month.',
    items: EVERYTHING,
    cta: 'Get started',
  },
  {
    name: 'Platform', price: '$997', setup: SETUP, featured: true,
    line: 'The whole platform, sized for a bigger store: every rep on the floor, a phone that rings all day, and a direct line to us.',
    items: ['Everything in Store', 'Every rep on your floor, no seat limit', 'About 1,000 phone minutes a month, with texts to match', 'We read your numbers every month and tune the site for more sales', 'A direct line to us, with changes to the site turned around in days'],
    cta: 'Get started',
  },
  // Enterprise (Phillip, 2026-11-02): the regional groups with dozens or
  // hundreds of stores, and brands that want the designer in every dealer's
  // hand. Contact us for price. The designer plugs into any website and any
  // database; integration is quoted.
  {
    name: 'Enterprise', price: 'Contact us', custom: true, setup: 'Integration and rollout quoted.',
    line: 'For groups with dozens or hundreds of stores, and for brands that want it in every dealer’s hand. One contract, priced by store.',
    items: ['Everything in Platform, in every store', 'The designer integrated into the website you already run, or as the website', 'One catalog across every location, each store’s own prices and stock', 'Every rep in every store, signed in and credited for the sale', 'One record per customer across your stores', 'Brands: your catalog in every dealer’s hand, sold the way you want it sold'],
    cta: 'Contact us',
  },
];

const PRICING_ADDONS = [
  { name: 'Phone agent on its own', line: 'Keeping your current website? The phone agent runs with the site you have. Answers every call, knows every product and every order, sells to new callers, transfers to staff on shift. A monthly allowance of minutes is included.', price: '$99', note: 'With the website you have' },
];

const LEAK_STATS = [
  {
    end: 393,
    suffix: '%',
    label: 'more shoppers sent to US retail sites by AI, year over year',
    src: 'Adobe Analytics, Q1 2026',
  },
  {
    end: 42,
    suffix: '%',
    label: 'better conversion from the shoppers AI sends',
    src: 'Adobe Analytics, March 2026',
  },
  {
    end: 39,
    suffix: '%',
    label: 'of US consumers have used AI to shop online',
    src: 'Adobe survey, 2026',
  },
  {
    end: 25,
    prefix: '15 to ',
    suffix: '%',
    label: 'of US online shopping could run through AI agents by 2030',
    src: 'Bain & Company, 2026',
  },
];

// How an AI assistant decides which store to name. Three things, each
// one the platform does for the store (the pre-render writes real HTML
// and JSON-LD per page, sitemaps and llms.txt; every delivery asks for
// the Google review).
const AI_FIRST_POINTS = [
  {
    title: 'A real page for every piece',
    line: 'Every piece you carry gets its own page, written as plain HTML with the price, the size and whether it is in stock. AI assistants and Google read it without having to run your site.',
  },
  {
    title: 'Facts a machine can trust',
    line: 'Structured data on every page: the product, the price, the store, the hours, the reviews. A sitemap and an llms.txt tell the assistants what you sell and where, in their own format.',
  },
  {
    title: 'Reviews that feed the answer',
    line: 'Every delivered order asks for the Google review. Assistants lean on how many reviews a store has and how recent they are when they pick one to name.',
  },
];

const STORY_STATS = {
  match: {
    value: 'Fit first',
    line: 'Most furniture shoppers need exact measurements before they trust that a piece will fit their home.',
    src: 'Baymard Institute furniture research, 2025',
  },
  ecom: {
    value: '79%',
    line: 'of furniture shoppers go to the product photo first. Online, the picture and the designer do the selling.',
    src: 'Baymard Institute furniture research, 2025',
  },
  agent: null,
};

// ============================================
// FAQ - visible section matching the FAQPage
// JSON-LD in index.html word for word. Edit both
// together or the schema and the page drift apart.
// ============================================
const FAQ_ITEMS = FURNITURE_FAQS.slice(0, 6).map(([q, a]) => ({ q, a }));

function useHeroVariant() {
  const [heroKey, setHeroKey] = useState(HERO_DEFAULT);
  const [showSwitcher, setShowSwitcher] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const param = new URLSearchParams(window.location.search).get('hero');
    if (!param) return;
    setShowSwitcher(true);
    const key = param.toUpperCase();
    if (HERO_COPY[key]) setHeroKey(key);
  }, []);

  return {
    hero: HERO_COPY[heroKey] || HERO_COPY[HERO_DEFAULT],
    heroKey,
    setHeroKey,
    showSwitcher,
  };
}

function HeroEyebrow({ hero, className = '' }) {
  return (
    <p className={className}>
      {hero.eyebrow}
      <span className="hidden xl:inline text-blue-600/50"> &middot; Website &middot; Designer &middot; Floor</span>
    </p>
  );
}

function HeroHeadline({ lines, className = '' }) {
  return (
    <h1 className={className}>
      {lines.map((line, i) => (
        <span
          key={line.text}
          className={
            line.gradient
              ? 'block text-blue-600 animate-slideUp'
              : 'block animate-slideUp'
          }
          style={{ animationDelay: `${i * 0.2}s` }}
        >
          {line.text}
        </span>
      ))}
    </h1>
  );
}

// One CTA row, rendered in two places (the mobile single-screen stack and the
// lg grid). `dense` trims the padding so both pills clear a 360px viewport
// side by side instead of stacking below the fold.
function HeroCtas({ calendlyUrl, dense = false }) {
  const pad = dense ? 'px-4 py-1.5' : 'px-5 md:px-6 py-2 md:py-2.5';
  const label = dense ? 'text-[13px]' : 'text-sm md:text-[15px]';
  const sub = dense ? 'text-[9px]' : 'text-[10px] md:text-[11px]';
  return (
    <div className={`flex flex-row flex-wrap ${dense ? 'gap-2' : 'gap-2.5 md:gap-3'} justify-center lg:justify-start animate-fadeIn`} style={{ animationDelay: '0.8s' }}>
      <Link
        to="/phone-agent#live-demo"
        className={`${pad} bg-blue-600 text-white rounded-full flex flex-col items-center justify-center leading-tight shadow-lg shadow-blue-600/25 hover:bg-blue-700 hover:shadow-blue-600/30 transition-all duration-300 hover:scale-[1.03]`}
      >
        <span className={`flex items-center gap-2 ${label} font-bold whitespace-nowrap`}>
          <Mic className="w-4 h-4 flex-shrink-0" />
          <span>Demo it now</span>
        </span>
        <span className={`${sub} font-semibold text-blue-100 tracking-wide whitespace-nowrap`}>
          Live demo · just talk
        </span>
      </Link>
      {/* The live store, not the booth: Vegas Market (July) is past, and the
          product is the thing to see (2026-10-29). */}
      <a
        href={CONTACT.liveSite}
        target="_blank"
        rel="noopener noreferrer"
        className={`group ${pad} bg-white border border-blue-600 text-blue-700 rounded-full hover:bg-blue-50 transition-all duration-300 flex flex-col items-center justify-center leading-tight`}
      >
        <span className={`flex items-center gap-2 ${label} font-semibold whitespace-nowrap`}>
          <Monitor className="w-4 h-4 flex-shrink-0" />
          <span>{dense ? 'See a live store' : 'See a live store'}</span>
        </span>
        <span className={`${sub} font-semibold text-slate-600 tracking-wide whitespace-nowrap`}>
          eastbay.direct · the whole platform, running
        </span>
      </a>
    </div>
  );
}

function HeroCopySwitcher({ heroKey, setHeroKey }) {
  return (
    <div className="fixed bottom-4 left-4 z-50 flex items-center gap-1 rounded-full border border-slate-200 bg-black/75 backdrop-blur px-2 py-1.5 shadow-xl">
      <span className="px-1.5 text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400">Hero</span>
      {Object.values(HERO_COPY).map((v) => (
        <button
          key={v.id}
          type="button"
          title={v.label}
          onClick={() => setHeroKey(v.id)}
          className={`px-3 py-1 rounded-full text-[11px] font-bold transition-colors ${
            heroKey === v.id ? 'bg-blue-500 text-slate-900' : 'text-slate-500 hover:text-slate-900 hover:bg-white/10'
          }`}
        >
          {v.id}
        </button>
      ))}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================
// ============================================
// WHAT WE DO - the services, one card each, in the order
// the page tells them. The first four are the product; the
// last three come with it.
// ============================================
function ServicesGrid() {
  // Pages for other kinds of business (restaurants) stay out of the
  // furniture story here; they are in the nav and the footer.
  const furniture = SERVICES.filter((s) => !s.group);
  const main = furniture.slice(0, 4);
  const included = furniture.slice(4);
  const card = (s, big) => {
    const Icon = s.icon;
    return (
      <Link
        key={s.slug}
        to={s.path}
        className={`group block rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_40px_-16px_rgba(15,23,42,0.14)] hover:border-blue-500 hover:bg-blue-50 transition-all ${big ? 'p-5 md:p-7' : 'p-4 md:p-5'}`}
      >
        <span className={`inline-flex items-center justify-center rounded-xl bg-blue-50 border border-blue-200 ${big ? 'w-10 h-10 mb-4' : 'w-8 h-8 mb-3'}`}>
          <Icon className={`${big ? 'w-5 h-5' : 'w-4 h-4'} text-blue-600`} />
        </span>
        <h3 className={`text-slate-900 font-bold tracking-tight ${big ? 'text-lg md:text-xl mb-1.5' : 'text-base mb-1'}`}>{s.name}</h3>
        <p className={`text-slate-500 leading-snug ${big ? 'text-sm' : 'text-xs md:text-sm'}`}>{s.blurb}</p>
        <span className="mt-3 inline-flex items-center gap-1 text-blue-600 text-xs font-semibold">
          Learn more <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
        </span>
      </Link>
    );
  };
  return (
    <section id="services" className="py-12 md:py-20 px-4 border-t border-slate-200">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-8 md:mb-12">
          <p className="text-blue-600 font-semibold uppercase tracking-[0.28em] text-[10px] md:text-xs mb-3 md:mb-4">What is inside</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]">
            One platform.{' '}
            <span className="text-blue-600">Each piece has its own page.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-500 max-w-2xl mx-auto">
            The store with every piece and checkout, the designer, the sales floor, the phone, the receipt, deliveries and the reviews. Every piece comes with both tiers.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-4 md:mb-5">
          {main.map((s) => card(s, true))}
        </div>
        <p className="text-slate-400 text-[10px] md:text-xs uppercase tracking-[0.28em] text-center my-5 md:my-6">Every piece comes with both tiers</p>
        <div className="grid sm:grid-cols-3 gap-3 md:gap-4">
          {included.map((s) => card(s, false))}
        </div>
      </div>
    </section>
  );
}

// QR code cells for the scan-to-take-it-home moment.
const QR_CELLS = [
  [0,0],[1,0],[2,0],[3,0],[4,0],[5,0],[6,0],[0,1],[6,1],[0,2],[2,2],[3,2],[4,2],[6,2],[0,3],[2,3],[3,3],[4,3],[6,3],[0,4],[2,4],[3,4],[4,4],[6,4],[0,5],[6,5],[0,6],[1,6],[2,6],[3,6],[4,6],[5,6],[6,6],
  [14,0],[15,0],[16,0],[17,0],[18,0],[19,0],[20,0],[14,1],[20,1],[14,2],[16,2],[17,2],[18,2],[20,2],[14,3],[16,3],[17,3],[18,3],[20,3],[14,4],[16,4],[17,4],[18,4],[20,4],[14,5],[20,5],[14,6],[15,6],[16,6],[17,6],[18,6],[19,6],[20,6],
  [0,14],[1,14],[2,14],[3,14],[4,14],[5,14],[6,14],[0,15],[6,15],[0,16],[2,16],[3,16],[4,16],[6,16],[0,17],[2,17],[3,17],[4,17],[6,17],[0,18],[2,18],[3,18],[4,18],[6,18],[0,19],[6,19],[0,20],[1,20],[2,20],[3,20],[4,20],[5,20],[6,20],
  [8,0],[10,0],[8,2],[9,3],[11,1],[12,4],[8,5],[10,6],[12,6],[9,8],[11,8],[13,8],[15,8],[17,9],[19,8],[8,9],[10,10],[12,10],[14,11],[16,12],[18,10],[20,12],[9,12],[11,12],[13,13],[8,14],[10,15],[12,16],[14,14],[16,16],[18,15],[20,16],[9,17],[11,18],[13,19],[15,18],[17,20],[19,19],[8,20],[10,20],
];

// ============================================
// SALES ASSISTANT DEMO - the floor, then the drive home. Same scaled frame
// and beat timeline as the website demo, so the three stories move alike.
// A customer checks in, the salesperson enters what she said, the options
// appear, the screen turns to her, a package is built, she scans the code,
// and that evening she texts from home while the next customer waits.
// ============================================
const SD_CUSTOMERS = [
  { name: 'Maria L.', wants: 'Living room · Sectional', since: 'Seated 2 min' },
  { name: 'Daniel R.', wants: 'Bedroom · King', since: 'Waiting' },
  { name: 'Priya S.', wants: 'Dining · 6 seats', since: 'Browsing' },
];
const SD_WANTS = ['Living room', 'Sectional', '110" wall', 'Grey', 'Fabric', 'Under $1,500'];
const SD_OPTIONS = [
  { name: 'Danville Sectional', price: '$1,480', img: 'danville-slate', note: 'Slate · 104" W · 4 seats', tag: 'Exact match', ok: true },
  { name: 'Rockridge Frameless', price: '$1,150', img: 'rockridge-fog', note: 'Fog · 98" W · 4 seats', tag: 'Exact match', ok: true },
  { name: 'Alameda Sectional', price: '$1,549', img: 'alameda-forest', note: 'Forest · 112" W · 5 seats', tag: '$49 over budget', ok: false },
];
const SD_PACKAGE = [
  { name: 'Danville Sectional', price: '$1,480', img: 'danville-slate' },
  { name: 'Claremont Ottoman', price: '$295', img: 'claremont-ottoman' },
];
const SD_ACTIONS = {
  0: 'Maria checked in at the front desk.',
  1: 'Entering what Maria said.',
  3: '3 options from 14 brands. Fit checked against a 110" wall.',
  4: 'Shared live to Maria’s portal. She picks.',
  5: 'Package built. Good for 14 days.',
  6: 'Maria leaves with her portal on her phone.',
  7: '7:42 pm. Maria texts from home. You answer from this screen.',
  8: 'Three customers. One screen.',
};
const SD_ACTION_KEYS = Object.keys(SD_ACTIONS).map(Number).sort((a, b) => b - a);

function TabletMockup({ children, caption }) {
  const { hostRef, w: designW, scale } = useScaledFrame();
  return (
    <div ref={hostRef} className="w-full lg:w-[580px] flex flex-col items-center">
      {caption && (
        <div className="lg:hidden mb-2.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-200">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400" aria-hidden="true" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600">{caption}</span>
        </div>
      )}
      <div className="relative max-w-none" style={{ width: designW * scale, maxWidth: 'none', height: SA_DESIGN_H * scale }}>
        <div className="absolute -inset-5 lg:-inset-8 bg-gradient-to-r from-blue-500/10 to-blue-500/10 rounded-[2.5rem] blur-2xl" aria-hidden="true" />
        <div className="absolute top-0 left-0 max-w-none" style={{ width: designW, maxWidth: 'none', height: SA_DESIGN_H, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          <div className="relative w-full h-full rounded-[22px] bg-gradient-to-b from-gray-800 to-gray-900 p-3 shadow-2xl border border-gray-700">
            <div className="relative w-full h-full rounded-xl overflow-hidden bg-gray-950">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SalesAssistantDemo({ isActive }) {
  const [beat, setBeat] = useState(0);
  const startedRef = useRef(false);
  const timersRef = useRef([]);

  useEffect(() => {
    if (!isActive || startedRef.current) return;
    startedRef.current = true;
    const TIMELINE = [
      { t: 0, b: 0 },       // the floor: three customers, Maria just seated
      { t: 1500, b: 1 },    // match a customer: her wants land as chips
      { t: 3300, b: 2 },    // (chips keep landing)
      { t: 5200, b: 3 },    // three options, fit checked
      { t: 8200, b: 4 },    // customer view: the screen turns to her
      { t: 10800, b: 5 },   // package: two pieces, your price, 14 days
      { t: 13300, b: 6 },   // scan: the list is on her phone
      { t: 16200, b: 7 },   // that evening: she texts, you answer
      { t: 19200, b: 8 },   // back to the floor: Daniel is next
    ];
    const clearAll = () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };
    const run = () => {
      clearAll();
      TIMELINE.forEach(({ t, b }) => timersRef.current.push(setTimeout(() => setBeat(b), t)));
    };
    run();
    return undefined;
  }, [isActive]);
  useEffect(() => () => { timersRef.current.forEach(clearTimeout); }, []);

  const action = SD_ACTIONS[SD_ACTION_KEYS.find((k) => beat >= k)];
  const chipsShown = beat === 1 ? 3 : beat >= 2 ? SD_WANTS.length : 0;
  const onFloor = beat === 0 || beat === 8;
  const customerView = beat === 4;
  const showPackage = beat >= 5 && beat <= 7;
  const showPhone = beat === 6 || beat === 7;
  const evening = beat === 7;

  return (
    <div className="absolute inset-0 flex bg-gray-950 text-white">
      {/* Customers rail */}
      <div className="w-[150px] flex-shrink-0 border-r border-white/[0.07] bg-gray-900/70 flex flex-col">
        <div className="px-3 h-11 flex items-center border-b border-white/[0.07]">
          <span className="text-[11px] font-bold tracking-[0.16em] text-white uppercase">In store</span>
        </div>
        <div className="p-2 space-y-1.5">
          {SD_CUSTOMERS.map((c, i) => {
            const active = (i === 0 && !onFloor) || (i === 0 && beat === 0) || (i === 1 && beat === 8);
            const home = i === 0 && beat >= 6;
            return (
              <div key={c.name} className={`rounded-lg px-2.5 py-2 border transition-all duration-500 ${active ? 'border-blue-400/60 bg-blue-500/15' : 'border-white/[0.06] bg-white/[0.02]'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-white">{c.name}</span>
                  {home && evening && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
                </div>
                <p className="text-[9px] text-gray-400 truncate">{c.wants}</p>
                <p className={`text-[9px] font-medium mt-0.5 ${home ? 'text-emerald-300' : i === 1 && beat === 8 ? 'text-blue-300' : 'text-gray-500'}`}>
                  {home ? (evening ? 'At home · 1 new text' : 'List on her phone') : i === 1 && beat === 8 ? 'Up next' : c.since}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main pane */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex-shrink-0 flex items-center justify-between px-4 h-11 border-b border-white/[0.07] bg-gray-900/70">
          <span className="text-[13px] font-bold tracking-tight text-white">
            {onFloor ? 'Sales assistant' : customerView ? 'Maria’s portal' : showPackage ? 'Maria’s package' : 'Match a customer'}
          </span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${customerView ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-200' : 'bg-blue-500/15 border-blue-400/30 text-blue-200'}`}>
            {customerView ? 'Customer portal' : evening ? '7:42 pm' : 'Maria L.'}
          </span>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2 px-4 h-[26px] bg-blue-500/10 border-b border-blue-400/20">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse flex-shrink-0" aria-hidden="true" />
          <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-blue-300 flex-shrink-0">Floor</span>
          <span key={action} className="text-[10px] text-gray-300 font-medium truncate animate-fadeIn">{action}</span>
        </div>

        <div className="flex-1 min-h-0 relative overflow-hidden">
          {/* The floor */}
          {onFloor && (
            <div className="p-4 animate-fadeIn">
              <p className="text-[11px] text-gray-400 mb-3">{beat === 8 ? 'Maria is set. Daniel is next.' : 'Maria just sat down. Tap her name to start.'}</p>
              <div className="grid grid-cols-3 gap-2.5">
                {SA_FLOOR.map((p) => (
                  <div key={p.name} className="rounded-lg border border-white/10 overflow-hidden">
                    <SaShot img={p.img} alt={p.name} className="h-[70px]" />
                    <div className="px-2 py-1.5 bg-white/[0.03]"><p className="text-[9px] text-gray-300 truncate">{p.name}</p><p className="text-[10px] font-bold">{p.price}</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Match a customer */}
          {!onFloor && !customerView && !showPackage && (
            <div className="p-4">
              <p className="text-[9px] uppercase tracking-[0.16em] text-gray-500 mb-2">What she said</p>
              <div className="flex flex-wrap gap-1.5 mb-3 min-h-[52px]">
                {SD_WANTS.slice(0, chipsShown).map((w, i) => (
                  <span key={w} className="px-2.5 py-1 rounded-full bg-blue-500/20 border border-blue-400/40 text-[10px] font-semibold text-blue-200 animate-fadeIn" style={{ animationDelay: `${(i % 3) * 160}ms`, animationFillMode: 'backwards' }}>{w}</span>
                ))}
              </div>
              {beat >= 3 ? (
                <div className="space-y-2">
                  {SD_OPTIONS.map((o, i) => (
                    <div key={o.name} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-2 animate-fadeIn" style={{ animationDelay: `${i * 120}ms`, animationFillMode: 'backwards' }}>
                      <SaShot img={o.img} alt={o.name} className="w-16 h-12 rounded-md flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] font-semibold truncate">{o.name}</p>
                        <p className="text-[10px] text-gray-400 truncate">{o.note}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-[12px] font-bold">{o.price}</p>
                        <span className={`inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${o.ok ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' : 'bg-amber-500/15 text-amber-300 border-amber-400/30'}`}>{o.tag}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-gray-500">Searching 1,240 pieces across 14 brands…</p>
              )}
            </div>
          )}

          {/* Customer view */}
          {customerView && (
            <div className="p-4 animate-fadeIn">
              <div className="grid grid-cols-3 gap-2.5">
                {SD_OPTIONS.map((o, i) => (
                  <div key={o.name} className={`rounded-lg border overflow-hidden ${i === 0 ? 'border-blue-400/70 ring-2 ring-blue-400/30' : 'border-white/10'}`}>
                    <SaShot img={o.img} alt={o.name} className="h-[104px]" />
                    <div className="px-2 py-1.5 bg-white/[0.03]">
                      <p className="text-[10px] font-semibold truncate">{o.name}</p>
                      <p className="text-[12px] font-bold">{o.price}</p>
                      <p className="text-[9px] text-gray-400 truncate">{o.note}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-gray-300"><span className="text-blue-300 font-semibold">Maria:</span> The first one. Does it come with an ottoman?</p>
            </div>
          )}

          {/* Package */}
          {showPackage && (
            <div className="p-4 animate-fadeIn">
              <div className="space-y-2 mb-3">
                {SD_PACKAGE.map((p) => (
                  <div key={p.name} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-2">
                    <SaShot img={p.img} alt={p.name} className="w-14 h-10 rounded-md flex-shrink-0" />
                    <p className="text-[12px] font-semibold flex-1 truncate">{p.name}</p>
                    <p className="text-[12px] font-bold">{p.price}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border border-blue-400/30 bg-blue-500/10 p-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-400">List $1,775 · <span className="text-white font-semibold">Your price $1,599</span></p>
                  <p className="text-[9px] text-blue-300 mt-0.5">Good for 14 days</p>
                </div>
                {beat === 5 ? (
                  <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-blue-500 text-white">Send package</span>
                ) : (
                  <div className="flex items-center gap-2">
                    <svg viewBox="0 0 21 21" width="34" height="34" aria-hidden="true" shapeRendering="crispEdges" className="rounded bg-white p-0.5">
                      {QR_CELLS.map(([x, y]) => <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#111" />)}
                    </svg>
                    <span className="text-[9px] text-gray-300 leading-tight">Scan to<br />take it home</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Her phone, sliding in from the right once she scans */}
          <div className={`absolute top-3 bottom-3 right-3 w-[172px] rounded-[18px] bg-gray-900 border-[5px] border-gray-700 shadow-2xl transition-all duration-700 ${showPhone ? 'translate-x-0 opacity-100' : 'translate-x-[120%] opacity-0'}`}>
            <div className="h-full rounded-[12px] bg-black flex flex-col overflow-hidden">
              <div className="px-3 pt-3 pb-2 border-b border-white/10">
                <p className="text-[8px] text-gray-500 uppercase tracking-widest">Maria’s phone</p>
                <p className="text-[11px] font-semibold">My portal · Bluebird Home</p>
              </div>
              <div className="p-2.5 space-y-1.5 flex-1">
                {SD_PACKAGE.map((p) => (
                  <div key={p.name} className="flex items-center gap-2 rounded-md bg-white/[0.05] p-1.5">
                    <SaShot img={p.img} alt="" className="w-9 h-7 rounded flex-shrink-0" />
                    <div className="min-w-0"><p className="text-[9px] font-semibold truncate">{p.name}</p><p className="text-[9px] text-gray-400">{p.price}</p></div>
                  </div>
                ))}
                <p className="text-[9px] text-blue-300 pt-1">Package $1,599 · 14 days</p>
                {evening && (
                  <div className="pt-2 space-y-1.5 animate-fadeIn">
                    <div className="ml-4 rounded-xl rounded-br-sm bg-blue-600 px-2 py-1.5 text-[9px] leading-snug">Does the sectional come in a lighter grey?</div>
                    <div className="mr-4 rounded-xl rounded-bl-sm bg-white/10 px-2 py-1.5 text-[9px] leading-snug">Yes, Fog. Sending a photo now.</div>
                  </div>
                )}
              </div>
              <div className="px-2.5 pb-2.5">
                <div className="h-6 rounded-full bg-white/[0.06] border border-white/10 flex items-center px-2"><span className="text-[8px] text-gray-500">Text the store</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// INCLUDED STRIP - the receipt and the reviews. Still here,
// no longer the headline.
// ============================================
// WHO WE ARE - the one section that says it plainly (Phillip, 2026-10-31:
// the sales and designer platform for furniture stores, the catalog kiosk
// replacement, is who we are; the phone, the receipt, deliveries and
// reviews are features). Three things a customer says on the floor, and
// what happens on the platform.
const WHO_WE_ARE = [
  {
    says: '\u201cI need a sectional for a 96 inch wall. Grey. Under $1,500.\u201d',
    then: 'Five details at once. The virtual designer narrows every brand you carry to the pieces that fit, with prices, while the customer is still in the chair.',
  },
  {
    says: '\u201cCan I think about it?\u201d',
    then: 'The list goes to their phone. They finish at home, same price, and the rep gets the credit.',
  },
  {
    says: '\u201cDo you have this online?\u201d',
    then: 'Every piece is online with a price and checkout. Start at home, finish on your floor, or the other way round.',
  },
];

function WhoWeAre() {
  return (
    <section id="who-we-are" className="py-14 md:py-24 px-4 border-t border-slate-200">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-blue-600 font-semibold uppercase tracking-[0.28em] text-[10px] md:text-xs mb-4 md:mb-5">Who we are</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.1] tracking-tight">
            A catalog kiosk is a catalog on a screen.<br className="hidden sm:block" />{' '}
            <span className="text-blue-600">This is a designer that sells.</span>
          </h2>
          <p className="mt-5 md:mt-6 text-slate-600 text-sm md:text-lg leading-relaxed">
            Say what the customer needs. Get the exact piece from every brand you carry, with the price. The list goes home on their phone.
          </p>
        </div>

        <div className="mt-9 md:mt-12 grid md:grid-cols-3 gap-4 md:gap-6">
          {WHO_WE_ARE.map((w) => (
            <div key={w.says} className="rounded-2xl bg-slate-50 border border-slate-200 p-5 md:p-7 flex flex-col">
              <p className="text-slate-900 font-bold text-lg md:text-xl leading-snug tracking-tight">{w.says}</p>
              <p className="mt-3 md:mt-4 text-slate-600 text-sm md:text-[15px] leading-relaxed">{w.then}</p>
            </div>
          ))}
        </div>

        <p className="mt-8 md:mt-10 text-center text-slate-500 text-sm md:text-base max-w-2xl mx-auto">
          <span className="text-slate-900 font-semibold">That is the platform.</span>{' '}
          The phone agent, the receipt, deliveries and reviews come with it.
        </p>
      </div>
    </section>
  );
}

// THE AI - explained plainly, right after who we are (Phillip, 2026-10-31:
// "there should be AI being explained, so people understand that we're at
// the leading edge of this... we want people to understand that we are
// unique"). Five places the one AI works in a furniture sale, each live
// today, and the difference from software that adds a chat box.
const AI_PLACES = [
  { where: 'On your floor', what: 'The customer says what they need. Real pieces come back from every brand you carry, with prices, in seconds.' },
  { where: 'On your website', what: 'Shoppers talk to the designer, by voice or text, and leave with a cart.' },
  { where: 'On your phone', what: 'Every call answered. The caller has the price and a photo by text before they hang up.' },
  { where: 'In the receipt', what: 'Customers ask the receipt where their order is. It answers, any hour. Your phone rings less.' },
  { where: 'In AI answers', what: 'A shopper asks ChatGPT or Google where to buy. Your store is the one named.' },
];

function AiInside() {
  return (
    <section id="the-ai" className="py-14 md:py-24 px-4 border-t border-slate-200 bg-slate-900 text-white">
      <div className="container mx-auto max-w-6xl">
        <div className="lg:grid lg:grid-cols-5 lg:gap-12 items-start">
          <div className="lg:col-span-2 text-center lg:text-left">
            <p className="text-blue-300 font-semibold uppercase tracking-[0.28em] text-[10px] md:text-xs mb-4 md:mb-5">The AI</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight text-white">
              One AI. Every part of the sale.
            </h2>
            <p className="mt-5 md:mt-6 text-slate-300 text-sm md:text-lg leading-relaxed">
              Most furniture software adds a chat box. Ours runs the whole sale. It knows every brand, every piece, every price and every open order.
            </p>
            <p className="mt-4 md:mt-5 text-white font-semibold text-sm md:text-lg leading-relaxed">
              Nobody in furniture is taking AI this far. It is live today.
            </p>
            <div className="mt-6 md:mt-8 flex flex-wrap gap-3 justify-center lg:justify-start">
              <a href={CONTACT.liveSite} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 bg-white text-slate-900 rounded-full font-semibold text-sm md:text-base hover:shadow-xl transition-all duration-300">
                <span>See it live</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link to="/phone-agent#live-demo" className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 border border-white/30 text-white rounded-full font-semibold text-sm md:text-base hover:bg-white/10 transition-all duration-300">
                <Mic className="w-4 h-4" />
                <span>Talk to it</span>
              </Link>
            </div>
          </div>

          <ol className="lg:col-span-3 mt-9 lg:mt-0 divide-y divide-white/10 border-y border-white/10">
            {AI_PLACES.map((a, i) => (
              <li key={a.where} className="py-4 md:py-5 grid grid-cols-[2.25rem_1fr] md:grid-cols-[3rem_11rem_1fr] gap-x-3 md:gap-x-5 items-baseline">
                <span className="text-blue-300 font-bold tabular-nums text-sm md:text-base">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-white font-bold text-base md:text-lg leading-snug tracking-tight">{a.where}</span>
                <p className="col-start-2 md:col-start-3 mt-1 md:mt-0 text-slate-300 text-sm md:text-[15px] leading-relaxed">{a.what}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

// The other businesses we build websites for. One card each, from the
// same INDUSTRIES list the nav uses, minus furniture, which is this page.
// One line, not a section (Phillip, 2026-10-30: the home page is the
// furniture platform; the nav's "Who we build for" and /websites carry
// the rest).
function OtherIndustries() {
  return (
    <section id="industries" className="py-8 md:py-10 px-4 border-t border-slate-200">
      <p className="container mx-auto max-w-3xl text-center text-sm md:text-base text-slate-500">
        <span className="text-slate-900 font-semibold">Not a furniture store?</span>{' '}
        We build websites for restaurants, clinics and local services too.{' '}
        <Link to="/websites" className="text-blue-600 font-semibold hover:underline whitespace-nowrap">See the sites</Link>
      </p>
    </section>
  );
}

export default function Furniture() {
  useEffect(() => { document.title = headFor('/').title; }, []);

  const contactInfo = {
    phone: "(415) 624-6941",
    phoneLink: "tel:+14156246941",
    calendlyUrl: "https://calendly.com/byars-jp5/30min"
  };

  const { hero, heroKey, setHeroKey, showSwitcher } = useHeroVariant();

  // Links from the rest of the site arrive as /#pricing; the router does
  // not scroll to a hash on its own.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, [hash]);

  const [matchRef, matchInView] = useInView();
  const [agentRef, agentInView] = useInView();
  const [ecomRef, ecomInView] = useInView();
  const [receiptRef, receiptInView] = useInView();
  const [reviewRef, reviewInView] = useInView();
  const [statsRef, statsInView] = useInView(0.3);

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {showSwitcher && <HeroCopySwitcher heroKey={heroKey} setHeroKey={setHeroKey} />}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-white focus:text-blue-700 focus:rounded-lg focus:font-semibold">Skip to main content</a>
      <SiteNav />

      <main id="main-content">

      {/* HERO SECTION
          Mobile is a single-screen stack: copy (auto) / phone (all remaining
          space) / CTAs (auto). The phone host is the only flex-1 child, so the
          mockup absorbs whatever height is left instead of pushing the buttons
          below the fold. lg+ keeps the original two-column grid. */}
      <section className="min-h-[100svh] [@media(min-height:760px)_and_(max-width:1023px)]:h-[100svh] xl:min-h-[85vh] flex items-stretch lg:items-center justify-center relative pt-14 lg:pt-28 xl:pt-32 pb-4 md:pb-20 lg:pb-24 px-4">
        <div className="w-full max-w-7xl 2xl:max-w-[88rem] mx-auto flex flex-col lg:block">

          {/* ---------- MOBILE / TABLET: one screen, no scrolling ---------- */}
          <div className="lg:hidden flex flex-1 min-h-0 flex-col w-full">
            <div className="shrink-0 text-center">
              <HeroHeadline
                lines={hero.headline}
                className="text-[26px] sm:text-4xl font-bold leading-[1.06] tracking-tight"
              />
              <p className="mt-2 text-[13px] sm:text-[15px] leading-snug text-slate-600 font-medium max-w-md mx-auto animate-fadeIn" style={{ animationDelay: '0.5s' }}>
                {hero.kicker ? <span className="text-slate-900 font-semibold">{hero.kicker}</span> : hero.subhead}
              </p>
            </div>

            {/* The phone takes every pixel the copy and CTAs don't need */}
            <div className="flex-1 min-h-0 w-full flex items-center justify-center py-2.5">
              <div className="relative w-full h-full flex items-center justify-center animate-fadeIn" style={{ animationDelay: '0.4s' }}>
                <div className="absolute inset-x-4 inset-y-8 bg-gradient-to-r from-blue-500/10 via-blue-500/10 to-blue-500/10 rounded-full blur-2xl pointer-events-none" />
                <PhoneMockup fit glowColor="from-blue-500/15 to-blue-500/15" caption="What the rep sees">
                  <VariablesCard />
                </PhoneMockup>
              </div>
            </div>

            <div className="shrink-0 pb-1">
              <HeroCtas calendlyUrl={contactInfo.calendlyUrl} dense />
            </div>
          </div>

          {/* ---------- DESKTOP: original two-column grid ---------- */}
          <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 items-center">

            {/* Phone Mockup */}
            <div className="flex justify-center items-center animate-fadeIn lg:order-2" style={{ animationDelay: '0.4s' }}>
              <div className="relative">
                {/* Glow effects behind phone */}
                <div className="absolute -inset-6 lg:-inset-10 bg-gradient-to-r from-blue-500/10 via-blue-500/10 to-blue-500/10 rounded-full blur-2xl lg:blur-3xl" />
        
                <PhoneMockup glowColor="from-blue-500/15 to-blue-500/15" caption="What the rep sees">
                  <VariablesCard />
                </PhoneMockup>
              </div>
            </div>

            {/* Text content */}
            <div className="text-left lg:order-1">
              <HeroHeadline
                lines={hero.headline}
                className="text-[2.75rem] xl:text-5xl 2xl:text-[3.25rem] font-bold leading-[1.08] tracking-tight mb-5"
              />

              <p className="text-lg text-slate-600 font-medium mb-8 max-w-xl animate-fadeIn" style={{ animationDelay: '0.5s' }}>
                {hero.subhead}
                {hero.kicker && <>{' '}<span className="text-slate-900 font-semibold">{hero.kicker}</span></>}
              </p>

              <HeroCtas calendlyUrl={contactInfo.calendlyUrl} />

            </div>
          </div>
        </div>

      </section>

      {/* CLIENT LOGOS - the stores we build for, sliding under the hero */}
      <ClientLogoStrip />


      {/* WHO WE ARE - the platform in plain words, before any feature */}
      <WhoWeAre />

      {/* THE AI - where it works, so the leading edge is plain */}
      <AiInside />

      {/* STEP 1: THE SALES FLOOR - told first because it is where the platform
          helps most (Phillip, 2026-10-30: a rep could not find a piece for the
          customer's variables; the kiosk only searches; the platform found
          real options). One still picture, no demo. */}
      <StorySection
        id="sales-assistant"
        step={1}
        kicker="In the store"
        title={<>The rep finds what fits<br /><span className="text-blue-600">while the customer is still in the chair.</span></>}
        description="A customer needs a sectional for a 96 inch wall, in grey, under $1,500. A catalog kiosk browses by category and sorts A to Z, so the rep scrolls and the customer waits. On the platform the rep gives the virtual designer every detail at once and gets the pieces that match from every brand you carry, with the price and the size, in seconds. The customer picks on your floor, the list goes to their phone, and the receipt follows by text. If they want to think about it, the list goes home with them and they finish from the couch. The rep gets the credit either way."
        stat={STORY_STATS.match}
        ctaHref="/sales-assistant"
        ctaLabel="How it works"
        sectionRef={matchRef}
        align="right"
      >
        <TabletMockup caption="What the rep sees">
          <VariablesCard wide />
        </TabletMockup>
      </StorySection>

      {/* STEP 2: BEFORE THEY WALK IN - the same catalog on the shopper's own
          phone, found through Google and AI */}
      <StorySection
        id="online-agent"
        step={2}
        kicker="Before they walk in"
        title={<>It starts on their phone<br /><span className="text-blue-600">before they reach your door.</span></>}
        description="Every piece you carry is online with its price and its size, written so Google and AI assistants send the shopper to you instead of the chain. The designer takes the room, the size, the colour and the budget and builds the set with them. They buy right there, or they save the list and come to sit on it. When they walk in, your rep opens that same list and picks up where the website left off."
        stat={STORY_STATS.ecom}
        ctaHref="https://eastbay.direct/"
        ctaLabel="See it live"
        sectionRef={ecomRef}
      />

      {/* STEP 3: AI PHONE AGENT - in both tiers, also on its own */}
      <StorySection
        id="phone-agent"
        step={3}
        kicker="On the phone"
        title={<>Every call becomes<br /><span className="text-blue-600">a visit or a sale.</span></>}
        description="Keep your number. Every call is answered on the first ring by an agent that knows your floor and every open order. New callers get the price and a photo by text while they are still on the line, saved under their number, so when they walk in the rep already has it. Returning customers are recognised, and a person on shift is one transfer away. It comes with both tiers, or runs on its own with the website you have."
        stat={STORY_STATS.agent}
        ctaHref="/phone-agent#live-demo"
        ctaLabel="Try it live"
        sectionRef={agentRef}
      />

      {/* FOUND BY AI - a feature of the platform, told after the platform
          (Phillip, 2026-10-31: the sales and designer platform is who we
          are; everything else is a feature). The argument, then how a
          store gets picked, then the researched numbers counted up on scroll. */}
      <section ref={statsRef} className="px-4 border-t border-slate-200 bg-slate-50">
        <div className="container mx-auto max-w-5xl py-12 md:py-20">
          <p className="text-center text-blue-600 font-semibold uppercase tracking-[0.28em] text-[10px] md:text-xs mb-4 md:mb-5">
            Also in the platform &middot; Found by AI
          </p>
          <h2 className="text-center text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.1] tracking-tight max-w-3xl mx-auto">
            Your website is not just for people anymore.<br className="hidden sm:block" />{' '}
            <span className="text-blue-600">It has to be read by AI.</span>
          </h2>
          <p className="mt-5 md:mt-6 text-center text-slate-600 text-sm md:text-lg leading-relaxed max-w-3xl mx-auto">
            Shoppers ask ChatGPT, Gemini and Google&rsquo;s AI for a sectional near them before they ever search. Those assistants do not look at your photos. They read your pages. A store they can read gets named. A store that is a picture of a catalog gets skipped.
          </p>

          <div className="mt-9 md:mt-12 grid sm:grid-cols-3 gap-4 md:gap-6">
            {AI_FIRST_POINTS.map((p) => (
              <div key={p.title} className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6">
                <p className="text-slate-900 font-bold text-base md:text-lg leading-snug">{p.title}</p>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{p.line}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 md:mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {LEAK_STATS.map((s, i) => (
              <LeakStat key={i} {...s} active={statsInView} index={i} />
            ))}
          </div>
          <p className="mt-7 md:mt-9 text-center text-slate-900 text-sm md:text-base font-semibold">
            Shoppers ask AI first, then decide on your site.{' '}
            <span className="text-slate-500 font-normal">One saved sale pays for months of Exitium.</span>
          </p>
          <div className="mt-6 md:mt-8 text-center">
            <Link to="/ai-search" className="group inline-flex items-center gap-2 px-6 py-3 bg-white border border-blue-600 text-blue-700 rounded-full font-semibold hover:bg-blue-50 transition-all duration-300">
              <span>How we do it</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* PRICING SECTION - two tiers sized by the store, the phone on its own below */}
      <section id="pricing" className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-blue-600 font-semibold uppercase tracking-[0.28em] text-[10px] md:text-xs mb-4 md:mb-5">Pricing</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              One platform. <span className="text-blue-600">Any size.</span>
            </h2>
            <p className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto">
              Everything is in every tier. One showroom picks by how many people work the floor and how much the phone rings. Enterprise groups and brands get one contract. Month to month, setup waived when you pay for the year.
            </p>
          </div>

          <div className="grid gap-4 md:gap-5 md:grid-cols-3 items-stretch max-w-6xl mx-auto">
            {PRICING_TIERS.map((t) => (
              <div
                key={t.name}
                className={`relative flex flex-col p-5 md:p-6 rounded-2xl ${t.featured ? 'bg-white border-2 border-blue-600 shadow-xl shadow-blue-600/10 md:-my-3 md:py-9' : 'bg-white border border-slate-200 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_40px_-16px_rgba(15,23,42,0.14)]'}`}
              >
                {t.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">
                    Recommended
                  </div>
                )}
                <p className="text-blue-600 font-semibold uppercase tracking-[0.2em] text-[10px] mb-3">{t.name}</p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-4xl font-bold text-slate-900">{t.price}</span>
                  {!t.custom && <span className="text-slate-400 text-sm font-semibold">/mo</span>}
                </div>
                <p className="text-slate-400 text-xs mt-1 mb-4">{t.setup}</p>
                <p className="text-slate-600 text-sm mb-4">{t.line}</p>
                <ul className="space-y-2 mb-6">
                  {t.items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600 text-xs leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={contactInfo.calendlyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-auto block w-full px-5 py-2.5 rounded-full font-semibold text-sm text-center transition-all duration-300 ${t.featured ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white border border-blue-600 text-blue-700 hover:bg-blue-50'}`}
                >
                  {t.cta}
                </a>
              </div>
            ))}
          </div>

          {/* ON ITS OWN - the phone agent for a store keeping its website */}
          <div className="mt-8 md:mt-10 bg-white border border-slate-200 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_40px_-16px_rgba(15,23,42,0.14)] p-5 md:p-6">
            <p className="text-blue-600 font-semibold uppercase tracking-[0.2em] text-[10px] mb-4">On its own</p>
            <div className="divide-y divide-slate-100">
              {PRICING_ADDONS.map((a) => (
                <div key={a.name} className="py-4 first:pt-0 last:pb-0 md:flex md:items-center md:justify-between md:gap-8">
                  <div className="min-w-0 mb-2 md:mb-0">
                    <p className="text-slate-900 font-semibold text-sm">{a.name}</p>
                    <p className="text-slate-500 text-xs leading-relaxed">{a.line}</p>
                  </div>
                  <div className="flex items-baseline gap-1.5 flex-shrink-0 md:text-right">
                    <span className="text-2xl font-bold text-slate-900">{a.price}</span>
                    <span className="text-slate-400 text-xs font-semibold">/mo</span>
                    <span className="text-slate-400 text-xs ml-2">{a.note}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SPECIAL REQUESTS - AI consulting and custom automations, kept small */}
          <div className="mt-4 md:mt-5 bg-blue-50/60 border border-blue-100 rounded-2xl p-5 md:p-6 md:flex md:items-center md:justify-between md:gap-8">
            <div className="min-w-0 mb-4 md:mb-0">
              <p className="text-blue-600 font-semibold uppercase tracking-[0.2em] text-[10px] mb-2">Special requests</p>
              <p className="text-slate-900 font-semibold text-sm md:text-base">Keeping what you run? We make your current setup work better with AI.</p>
              <p className="text-slate-500 text-xs md:text-sm leading-relaxed mt-1">AI on the website and POS you already have, plus other automations for how your store runs: follow-ups, order updates, reports, whatever eats your staff's day. Scoped and quoted in one working session.</p>
            </div>
            <a href={contactInfo.calendlyUrl} target="_blank" rel="noopener noreferrer" className="inline-block flex-shrink-0 px-6 py-2.5 bg-white border border-blue-600 text-blue-700 hover:bg-blue-50 rounded-full font-semibold text-sm transition-all duration-300 text-center whitespace-nowrap">
              Book a working session
            </a>
          </div>

          <div className="mt-6 md:mt-8 space-y-2 text-sm text-slate-500 text-center max-w-3xl mx-auto">
            <p>
              <span className="text-slate-900 font-semibold">Keeping your current website?</span> The phone agent runs on its own with the site you have, $99 a month for most stores. Larger stores are quoted.
            </p>
            <p>
              <span className="text-slate-900 font-semibold">Three or more showrooms?</span> One agreement for all of them, unlimited calls, texts and screens, custom reporting, and a direct line to us. Setup and monthly cost quoted.
            </p>
            <p>
              Each additional showroom is 60% of your tier. Store covers up to three reps and about 300 phone minutes a month, Platform every rep and about 1,000 minutes, each with texts to match. A store that runs past its allowance is quoted a little more, or moves up a tier.
            </p>
          </div>
        </div>
      </section>

      {/* NOT A FURNITURE STORE - the other businesses we build for, one
          door each to the websites page. The furniture platform is the home
          page (Phillip, 2026-10-29: ecommerce for furniture is the main
          thing); the rest of the company is this one section. */}
      <OtherIndustries />

      {/* FAQ */}
      <FAQSection />

      {/* FINAL CTA */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-slate-900 rounded-2xl md:rounded-3xl p-6 md:p-12 text-center text-white">
            <p className="text-blue-300 font-semibold uppercase tracking-[0.28em] text-[10px] md:text-xs mb-4 md:mb-5">In the store · Online</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-4 md:mb-6 text-white">
              The virtual designer{' '}
              <span className="text-blue-300">for furniture stores.</span>
            </h2>
            <p className="text-lg md:text-xl text-slate-300 mb-6 md:mb-8 max-w-2xl mx-auto">
              One showroom, a group of fifty, or the brand that supplies them. Book 30 minutes and bring your catalog. Say what a customer needs and watch real pieces come back, then the sale close on your floor or at home.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
              <a href={contactInfo.calendlyUrl} target="_blank" rel="noopener noreferrer" className="group px-6 md:px-8 py-3 md:py-4 bg-white text-slate-900 rounded-full text-base md:text-lg font-bold hover:shadow-2xl transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2">
                <Calendar className="w-4 md:w-5 h-4 md:h-5" />
                <span>Book a Demo</span>
              </a>
              <a href={contactInfo.calendlyUrl} target="_blank" rel="noopener noreferrer" className="group px-6 md:px-8 py-3 md:py-4 bg-transparent border border-white/30 text-white rounded-full text-base md:text-lg font-semibold hover:bg-white/10 transition-all duration-300 flex items-center justify-center space-x-2">
                <span>Talk to sales</span>
                <ArrowRight className="w-4 md:w-5 h-4 md:h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      </main>

      <Sources ids={['S1', 'S2', 'S3', 'S7', 'S8']} />
      <SiteFooter />

      {/* Custom CSS animations */}
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }

        .animate-slideInRight {
          animation: slideInRight 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes vegasSpin {
          to { transform: rotate(360deg); }
        }
        
        @keyframes vegasGlow {
          0%, 100% { opacity: 0.45; transform: scale(0.98); }
          50% { opacity: 0.85; transform: scale(1.02); }
        }
        
        @keyframes vegasShine {
          0% { transform: translateX(-150%) skewX(-20deg); }
          55% { transform: translateX(650%) skewX(-20deg); }
          100% { transform: translateX(650%) skewX(-20deg); }
        }
        
        @media (prefers-reduced-motion: reduce) {
          .vegas-ring, .vegas-glow, .vegas-shine { animation: none !important; }
        }
        
        .animate-slideUp {
          animation: slideUp 0.6s ease-out forwards;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
        }
        
        .animate-gradient {
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  );
}